import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Location } from 'react-router-dom';
import { format } from 'date-fns';
import { Button, OverlayTrigger, Tooltip as BootstrapTooltip, Table, Badge, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWallet, faSync, faCheckCircle, faClock, faSave, 
  faBuilding, faExchangeAlt, faEdit, faTrashAlt, 
  faChartPie, faTimes,
  faMoneyBillWave, faCoins, faCopy, faBolt, faClone, faTimes as faClose,
  faChevronUp, faChevronDown, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { recordService, categoryService, exchangeRateService, dashboardService, cacheService } from '../../services/api';
import { FinancialRecord, Category, ExchangeRate, RecordType, AccountType, Currency } from '../../types';
import { useLLMParsing, LLMParseResult } from '../../hooks/useLLMParsing';
import RecordForm from './RecordForm';
import ImportPaymentsDialog from './ImportPaymentsDialog';
import RecordFilters from './RecordFilters';
import Calculator from '../common/Calculator';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../styles/Records.css';
import '../../styles/Records-mobile.css';

// Define custom location state interface
interface LocationState {
  filters?: {
    startDate?: string | Date;
    endDate?: string | Date;
    categoryId?: number | number[];
    recordType?: RecordType;
    accountType?: AccountType;
  };
}

// Interface for category summary data
interface CategorySummary {
  category_id: number;
  record_type: RecordType;
  total_amount: number;
  total_tax: number;
  // New fields from backend fix
  total_amount_vnd?: number;
  total_amount_usd?: number;
  total_tax_vnd?: number;
  total_tax_usd?: number;
  // Report amount fields (amount + tax)
  total_report_vnd?: number;
  total_report_usd?: number;
  record_count: number;
  Category: {
    name: string;
    type: string;
    account_type: AccountType;
  };
}

// Interface for form data
interface FormDataType {
  recordType: RecordType;
  accountType: AccountType;
  date: string;
  description: string;
  amountVnd: string;
  amountUsd: string;
  taxPercent: string;
  taxVnd: string;
  taxUsd: string;
  categoryId: string;
}

const Records: React.FC = () => {
  const location = useLocation() as Location & { state: LocationState };
  
  // State for records
  const [records, setRecords] = useState<FinancialRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialized, setInitialized] = useState<boolean>(false);

  // Ref to prevent duplicate initialization in React StrictMode
  const initializationRef = useRef<boolean>(false);

  // Ref for focusing on description input
  const descriptionInputRef = useRef<HTMLInputElement>(null);

  // State for totals - used for summary calculations
const [totals, setTotals] = useState({ total_vnd: 0, total_usd: 0, total_tax_vnd: 0, total_tax_usd: 0 });
  
  // State for current balance (all transactions)
  const [currentBalance, setCurrentBalance] = useState({ total_vnd: 0, total_usd: 0 });
  const [balanceLoading, setBalanceLoading] = useState<boolean>(false);
  const [balanceFromCache, setBalanceFromCache] = useState<boolean>(false);
  const [balanceFresh, setBalanceFresh] = useState<boolean>(true);
  
  // State for notifications
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
    show: boolean;
  }>({
    message: '',
    type: 'success',
    show: false
  });

  // Notification helper function
  const showNotification = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setNotification({ message, type, show: true });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 4000);
  };
  
  // State for exchange rate update warning
  const [showExchangeRateWarning, setShowExchangeRateWarning] = useState<boolean>(false);
  const [pendingExchangeRate, setPendingExchangeRate] = useState<number | null>(null);
  
  // State for copy operation
  const [copyLoading, setCopyLoading] = useState<boolean>(false);
  const [showCopyDialog, setShowCopyDialog] = useState<boolean>(false);
  const [copyOption, setCopyOption] = useState<'expense-only' | 'all'>('expense-only');
  const [copyFromMonth, setCopyFromMonth] = useState<string>('');
  const [copyToMonth, setCopyToMonth] = useState<string>('');
  
  // State for copied records (to highlight them until page refresh)
  const [copiedRecordIds, setCopiedRecordIds] = useState<Set<number>>(new Set());
  
  // State for bulk delete
  const [selectedRecordIds, setSelectedRecordIds] = useState<Set<number>>(new Set());
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState<boolean>(false);
  
  // State for import customer payments dialog
  const [showImportDialog, setShowImportDialog] = useState<boolean>(false);
  const [importData, setImportData] = useState<string>('');
  const [importLoading, setImportLoading] = useState<boolean>(false);
  const [parsedImportData, setParsedImportData] = useState<any[]>([]);
  const [duplicateRecords, setDuplicateRecords] = useState<any[]>([]);
  const [duplicateHandling, setDuplicateHandling] = useState<'skip' | 'override'>('skip');
  const [importExchangeRate, setImportExchangeRate] = useState<string>('');
  const [importExchangeRateId, setImportExchangeRateId] = useState<number | null>(null);
  const [appliedExchangeRate, setAppliedExchangeRate] = useState<number | null>(null);
  
  // State for import expense payments dialog
  const [showExpenseImportDialog, setShowExpenseImportDialog] = useState<boolean>(false);
  const [expenseImportData, setExpenseImportData] = useState<string>('');
  const [expenseImportLoading, setExpenseImportLoading] = useState<boolean>(false);
  const [parsedExpenseImportData, setParsedExpenseImportData] = useState<any[]>([]);
  const [expenseDuplicateRecords, setExpenseDuplicateRecords] = useState<any[]>([]);
  const [expenseDuplicateHandling, setExpenseDuplicateHandling] = useState<'skip' | 'override'>('skip');
  const [expenseImportExchangeRate, setExpenseImportExchangeRate] = useState<string>('');
  const [expenseImportExchangeRateId, setExpenseImportExchangeRateId] = useState<number | null>(null);
  const [expenseAppliedExchangeRate, setExpenseAppliedExchangeRate] = useState<number | null>(null);
  const [expenseExcludedRecords, setExpenseExcludedRecords] = useState<Set<number>>(new Set());
  const [showExpenseTextarea, setShowExpenseTextarea] = useState<boolean>(true);
  
  // State for modal and form
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<FinancialRecord | null>(null);
  
  // State for form data
  const [formData, setFormData] = useState({
    recordType: 'expense' as RecordType,
    accountType: 'company' as AccountType,
    date: new Date().toISOString().split('T')[0],
    description: '',
    amountVnd: '0',
    amountUsd: '0.0',
    taxPercent: '0',
    taxVnd: '0',
    taxUsd: '0.0',
    categoryId: ''
  });
  
  // Track if description was changed by user (for auto-parse)
  const [descriptionChanged, setDescriptionChanged] = useState(false);
  
  // Helper function to get current month date range
  const getCurrentMonthRange = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { startOfMonth, endOfMonth };
  };

  // State for filters - initialize with current month as default
  const [filters, setFilters] = useState(() => {
    const { startOfMonth, endOfMonth } = getCurrentMonthRange();
    return {
      startDate: startOfMonth,
      endDate: endOfMonth,
      recordType: '' as RecordType | '',
      accountType: '' as AccountType | '',
      categoryId: [] as number[], // Changed to array for multiple selection
      search: '',
      sortBy: 'record_date',
      sortOrder: 'desc',
      minAmount: '',
      maxAmount: '',
      amountCurrency: 'vnd' as 'vnd' | 'usd'
    };
  });
  
  // State for supporting data
  const [categories, setCategories] = useState<Category[]>([]);
  const [currentRate, setCurrentRate] = useState<ExchangeRate | null>(null);
  const [newExchangeRate, setNewExchangeRate] = useState<string>('0.00');
  const [editingExchangeRate, setEditingExchangeRate] = useState<ExchangeRate | null>(null);
  const [originalExchangeRate, setOriginalExchangeRate] = useState<ExchangeRate | null>(null); // Track original exchange rate before edits
  // pendingExchangeRate is already declared on line 81
  // showExchangeRateWarning is already declared on line 80
  const [lastModifiedField, setLastModifiedField] = useState<string>('');
  
  // State for category summary
  const [categorySummary, setCategorySummary] = useState<CategorySummary[]>([]);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);

  // State for loading indicators
  // loading, initialized, and balanceLoading are already declared above
  
  // State for calculator
  const [calculatorState, setCalculatorState] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    targetField: '',
    targetRef: null as HTMLInputElement | null
  });

  // Formula validation state
  const [formulaErrors, setFormulaErrors] = useState<{[key: string]: boolean}>({});
  
  // Track previous values to detect actual changes
  const [previousFormValues, setPreviousFormValues] = useState({
    amountVnd: '0',
    amountUsd: '0.0',
    taxPercent: '0',
    taxVnd: '0',
    taxUsd: '0.0'
  });
  
  // Track focus state for select-all behavior
  const [fieldFocusState, setFieldFocusState] = useState<{[key: string]: boolean}>({});
  
  // Notification state and function are already declared above

  // Initialize LLM parsing hook
  const {
    isLoading: isLLMLoading,
    parseDescription: parseLLMDescription,
    clearState: clearLLMState,
    isServiceAvailable: isLLMServiceAvailable
  } = useLLMParsing({
    minDescriptionLength: 10,
    minConfidence: 0.6,
    recordType: formData.recordType,
    accountType: formData.accountType,
    onParseComplete: (result: LLMParseResult) => {
      handleLLMParseComplete(result);
    }
  });



  // Notification helper function is already declared above
  
  // Close notification manually
  const closeNotification = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };

  // Handle LLM parse completion
  const handleLLMParseComplete = (result: LLMParseResult) => {
    const rateToUse = editingExchangeRate || currentRate;
    if (!rateToUse) return;

    let updates: any = {};
    let hasUpdates = false;
    let unparsedFields: string[] = [];

    // Auto-fill amounts based on detected currency
    if (result.amount_vnd && result.currency_detected === 'VND') {
      const vndFormatted = result.amount_vnd.toLocaleString('en-US', { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0 
      });
      updates.amountVnd = vndFormatted;
      
      // Calculate USD from VND
      const usdAmount = result.amount_vnd / rateToUse.vnd_to_usd;
      updates.amountUsd = usdAmount.toLocaleString('en-US', { 
        minimumFractionDigits: 1, 
        maximumFractionDigits: 1 
      });
      hasUpdates = true;
    } else if (result.amount_usd && result.currency_detected === 'USD') {
      const usdFormatted = result.amount_usd.toLocaleString('en-US', { 
        minimumFractionDigits: 1, 
        maximumFractionDigits: 1 
      });
      updates.amountUsd = usdFormatted;
      
      // Calculate VND from USD
      const vndAmount = Math.round(result.amount_usd * rateToUse.vnd_to_usd);
      updates.amountVnd = vndAmount.toLocaleString('en-US', { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0 
      });
      hasUpdates = true;
    } else {
      // Amount not parsed - need to focus on amount fields
      unparsedFields.push('amountVnd');
    }

    // Auto-fill tax percentage
    if (result.tax_percent && result.tax_percent > 0) {
      updates.taxPercent = result.tax_percent.toString();
      hasUpdates = true;

      // Calculate tax amounts if we have main amounts
      const vndAmount = result.amount_vnd || (result.amount_usd ? Math.round(result.amount_usd * rateToUse.vnd_to_usd) : 0);
      const usdAmount = result.amount_usd || (result.amount_vnd ? result.amount_vnd / rateToUse.vnd_to_usd : 0);

      if (vndAmount > 0 && usdAmount > 0) {
        const { taxVnd, taxUsd } = calculateTaxAmounts(vndAmount, usdAmount, result.tax_percent, rateToUse, formData.recordType);
        updates.taxVnd = taxVnd;
        updates.taxUsd = taxUsd;
      }
    }

    // Auto-select suggested category
    if (result.suggested_category_id) {
      // Verify the category exists in the current categories list
      const suggestedCategory = categories.find(cat => 
        cat.id === result.suggested_category_id && 
        cat.type === formData.recordType && 
        cat.account_type === formData.accountType
      );
      
      if (suggestedCategory) {
        updates.categoryId = result.suggested_category_id.toString();
        hasUpdates = true;
      } else {
        // Category not found or invalid - need to focus on category
        unparsedFields.push('categoryId');
      }
    } else {
      // Category not parsed - need to focus on category
      unparsedFields.push('categoryId');
    }

    // Update description with cleaned version if available
    if (result.cleaned_description && result.cleaned_description.trim()) {
      updates.description = result.cleaned_description.trim();
      hasUpdates = true;
    }

    // Apply updates if any
    if (hasUpdates) {
      setFormData(prev => ({ ...prev, ...updates }));
      
      // Update previous form values to prevent false "changes"
      setPreviousFormValues(prev => ({
        ...prev,
        ...Object.keys(updates).reduce((acc, key) => {
          if (key === 'amountVnd') acc.amountVnd = updates[key];
          if (key === 'amountUsd') acc.amountUsd = updates[key];
          if (key === 'taxPercent') acc.taxPercent = updates[key];
          if (key === 'taxVnd') acc.taxVnd = updates[key];
          if (key === 'taxUsd') acc.taxUsd = updates[key];
          return acc;
        }, {} as any)
      }));

      // Show success notification with confidence
      const confidencePercent = Math.round((result.confidence || 0) * 100);
      showNotification(`✅ Parsed successfully (${confidencePercent}% confidence)`, 'success');
    }

    // Handle focus management after parsing
    setTimeout(() => {
      if (unparsedFields.length === 0) {
        // All fields parsed successfully - focus on Save button
        const saveButton = document.querySelector('.btn-save') as HTMLButtonElement;
        if (saveButton) {
          saveButton.focus();
        }
      } else {
        // Focus on the first unparsed field
        const firstUnparsedField = unparsedFields[0];
        let targetElement: HTMLElement | null = null;

        if (firstUnparsedField === 'categoryId') {
          targetElement = document.querySelector('select[title="Select category"]') as HTMLSelectElement;
        } else if (firstUnparsedField === 'amountVnd') {
          targetElement = document.querySelector('.amount-input-vnd') as HTMLInputElement;
        } else if (firstUnparsedField === 'amountUsd') {
          targetElement = document.querySelector('.amount-input-usd') as HTMLInputElement;
        }

        if (targetElement) {
          targetElement.focus();
        }
      }
    }, 100);
  };

  // Handle description blur to trigger LLM parsing only if user changed
  const handleDescriptionBlur = async () => {
    if (formData.description.trim() && isLLMServiceAvailable() && descriptionChanged) {
      clearLLMState();
      setTimeout(() => {
        parseLLMDescription(formData.description);
        setDescriptionChanged(false); // Reset after auto-parse
      }, 100);
    }
  };

  // Handle manual parse button click
  const handleManualParse = () => {
    if (formData.description.trim()) {
      clearLLMState();
      parseLLMDescription(formData.description);
    }
  };

  // Handle Enter key press in form fields
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFormSave();
    }
  };

  // Handle CTRL+Space for calculator
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, fieldName: string) => {
    if (e.ctrlKey && e.key === ' ') {
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      setCalculatorState({
        isOpen: true,
        position: { x: rect.left, y: rect.bottom },
        targetField: fieldName,
        targetRef: e.currentTarget
      });
      
      if (calculatorState.targetRef) {
        setTimeout(() => {
          calculatorState.targetRef?.focus();
        }, 100);
      }
    }
  };

  // Formula calculation helper
  const calculateFormula = (formula: string): { result: string; isValid: boolean } => {
    try {
      // Remove the "=" sign and sanitize the formula
      const cleanFormula = formula.slice(1).replace(/[^0-9+\-*/().]/g, '');
      if (!cleanFormula) {
        return { result: '', isValid: false };
      }
      
      // Using eval in a controlled way since we already sanitized the input
      // eslint-disable-next-line no-eval
      const evalResult = eval(cleanFormula);
      if (typeof evalResult === 'number' && !isNaN(evalResult) && isFinite(evalResult)) {
        return { result: Math.abs(evalResult).toString(), isValid: true };
      } else {
        return { result: '', isValid: false };
      }
    } catch (error) {
      return { result: '', isValid: false };
    }
  };

  // Check if a value is a formula
  const isFormula = (value: string): boolean => {
    return value.startsWith('=');
  };

  // Check if tax amount fields should be visible
  const shouldShowTaxAmounts = () => {
    const taxPercent = parseFloat(formData.taxPercent) || 0;
    return taxPercent > 0;
  };

  // Handle tax percent change with visibility logic
  const handleTaxPercentChangeWithVisibility = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLastModifiedField('taxPercent');
    
    // If tax percent becomes zero or empty, reset tax amounts to zero
    const taxPercent = parseFloat(value) || 0;
    if (taxPercent === 0) {
      setFormData((prev: typeof formData) => ({ 
        ...prev, 
        taxPercent: value,
        taxVnd: '0',
        taxUsd: '0.0'
      }));
    } else {
      setFormData((prev: typeof formData) => ({ ...prev, taxPercent: value }));
    }
    
    // Set previous values for focus tracking
    setPreviousFormValues((prev: typeof previousFormValues) => ({ ...prev, taxPercent: formData.taxPercent }));
  };

  // Single initialization function to load all required data
  const initializeData = async () => {
    // Prevent duplicate initialization calls (React StrictMode causes double mounting)
    if (initializationRef.current) {
      return;
    }
    initializationRef.current = true;
    
    setLoading(true);
    
    // Initialize with safe defaults
    setCategories([]);
    setCurrentRate(null);
    
    try {
      // Fetch all required data in parallel
      const [categoriesResult, exchangeRateResult] = await Promise.all([
        categoryService.getAll(),
        exchangeRateService.getCurrent()
      ]);

      // Handle categories response safely
      const categoriesData = categoriesResult.data?.data || categoriesResult.data || [];
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      
      // Handle exchange rate response safely
      const exchangeRateData = exchangeRateResult.data?.data || exchangeRateResult.data;
      setCurrentRate(exchangeRateData);
      
      // Set exchange rate input value
      if (exchangeRateData && exchangeRateData.vnd_to_usd) {
        setNewExchangeRate(formatNumber(exchangeRateData.vnd_to_usd));
      }
      
      // Now fetch records with default filters
      await fetchRecords();
      
      // Fetch current balance asynchronously (doesn't block initialization)
      fetchCurrentBalance();
      
      setInitialized(true);
    } catch (error) {
      console.error('Error initializing data:', error);
      showNotification('Error loading data. Please refresh the page.', 'error');
      setLoading(false);
    }
  };

  // Effect to initialize data on component mount
  useEffect(() => {
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to handle navigation filters from dashboard
  useEffect(() => {
    if (location.state?.filters && initialized) {
      const navFilters = location.state.filters;
      
      // Update filters with dashboard navigation filters
      setFilters((prev: typeof filters) => {
        const newFilters = { ...prev };
        
        // Handle date filters
        if (navFilters.startDate) {
          newFilters.startDate = new Date(navFilters.startDate);
        }
        if (navFilters.endDate) {
          newFilters.endDate = new Date(navFilters.endDate);
        }
        
        // Handle category filter
        if (navFilters.categoryId) {
          // Convert single category ID to array format
          newFilters.categoryId = typeof navFilters.categoryId === 'number' 
            ? [navFilters.categoryId] 
            : (Array.isArray(navFilters.categoryId) ? navFilters.categoryId : []);
        }
        
        // Handle record type filter
        if (navFilters.recordType) {
          newFilters.recordType = navFilters.recordType;
        }
        
        // Handle account type filter
        if (navFilters.accountType) {
          // Update form data account type as well
          setFormData((prevForm: typeof formData) => ({
            ...prevForm,
            accountType: navFilters.accountType
          }));
        }
        
        return newFilters;
      });
      
      // Show notification about applied filters
      showNotification('Filters applied from dashboard navigation', 'success');
      
      // Clear the navigation state to prevent reapplying filters on component re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state, initialized]);

  // Effect to focus on description input when page loads (only once on initial load)
  useEffect(() => {
    if (initialized && !loading && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized]);

  // Effect to fetch records when filters change (but only after initialization)
  // Note: minAmount, maxAmount, and amountCurrency are excluded from dependencies
  // so they only apply when user clicks the search button
  useEffect(() => {
    if (initialized) {
      fetchRecords();
    }
  }, [
    filters.startDate, 
    filters.endDate, 
    filters.categoryId, 
    filters.recordType, 
    filters.search,
    formData.accountType
  ]);

  // Effect to update exchange rate input when current rate changes
  useEffect(() => {
    if (currentRate) {
      setNewExchangeRate(formatNumber(currentRate.vnd_to_usd));
    }
  }, [currentRate]);

  // Effect to fetch current balance when account type changes
  useEffect(() => {
    if (initialized) {
      fetchCurrentBalance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.accountType]);

  // No auto-calculation - users will double-click to manually trigger recalculation
  
  // Calculator handler functions
  const handleCalculatorClose = () => {
    setCalculatorState((prev: typeof calculatorState) => ({
      ...prev,
      isOpen: false
    }));
  };

  const handleCalculatorResult = (result: string) => {
    // Use the targetField from calculatorState
    const fieldName = calculatorState.targetField;
    setLastModifiedField(fieldName);
    setFormData((prev: typeof formData) => ({
      ...prev,
      [fieldName]: result
    }));
    handleCalculatorClose();
  };

  // Import TaxCalculatorResult type from Calculator component
  const handleTaxCalculatorResult = (result: { netAmount: string; taxPercent: string; grossAmount: string }) => {
    setLastModifiedField('taxPercent');
    setFormData((prev: typeof formData) => ({
      ...prev,
      taxPercent: result.taxPercent
    }));
    handleCalculatorClose();
  };

  // Separate function for calculating tax amounts
  const calculateTaxAmounts = (vndAmount: number, usdAmount: number, taxPercent: number, exchangeRate?: ExchangeRate, recordType?: RecordType) => {
    const taxDecimal = taxPercent / 100;
    let taxVnd = '0';
    let taxUsd = '0.0';
    
    // Use the provided recordType or fall back to formData.recordType
    const typeToUse = recordType || formData.recordType;
    
    if (taxPercent > 0) {
      // Use provided exchange rate or fall back to editing/current rate
      const rateToUse = exchangeRate || editingExchangeRate || currentRate;
      
      if (vndAmount > 0) {
        let taxVndAmount: number;
        
        // Different calculation for income vs expense
        if (typeToUse === 'income') {
          // For income: entered amount is gross (includes tax)
          // Net = Gross / (1 + tax%)
          // Tax = Gross - Net
          taxVndAmount = Math.round(vndAmount - (vndAmount / (1 + taxDecimal)));
        } else {
          // For expense: entered amount is net (before tax)
          // Tax = Net * tax%
          taxVndAmount = Math.round(vndAmount * taxDecimal);
        }
        
        taxVnd = taxVndAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        
        // Calculate tax USD by converting tax VND using exchange rate
        if (rateToUse) {
          const taxUsdAmount = parseFloat((taxVndAmount / rateToUse.vnd_to_usd).toFixed(1));
          taxUsd = taxUsdAmount.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        }
      } else if (usdAmount > 0 && rateToUse) {
        let taxUsdAmount: number;
        
        // Different calculation for income vs expense
        if (typeToUse === 'income') {
          // For income: entered amount is gross (includes tax)
          taxUsdAmount = parseFloat((usdAmount - (usdAmount / (1 + taxDecimal))).toFixed(1));
        } else {
          // For expense: entered amount is net (before tax)
          taxUsdAmount = parseFloat((usdAmount * taxDecimal).toFixed(1));
        }
        
        taxUsd = taxUsdAmount.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        
        // Calculate tax VND by converting tax USD using exchange rate
        const taxVndAmount = Math.round(taxUsdAmount * rateToUse.vnd_to_usd);
        taxVnd = taxVndAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }
    }
    
    return { taxVnd, taxUsd };
  };

  // Auto-calculate tax amounts when tax percent changes
  useEffect(() => {
    if (lastModifiedField === 'taxPercent') {
      const taxPercent = parseFloat(formData.taxPercent) || 0;
      const vndAmount = parseFloat(formData.amountVnd.replace(/,/g, '')) || 0;
      const usdAmount = parseFloat(formData.amountUsd) || 0;
      
      // Use editing exchange rate if editing, otherwise use current rate
      const rateToUse = editingExchangeRate || currentRate;
      const { taxVnd, taxUsd } = calculateTaxAmounts(vndAmount, usdAmount, taxPercent, rateToUse || undefined, formData.recordType);
      
      if (taxVnd !== formData.taxVnd || taxUsd !== formData.taxUsd) {
        setFormData(prev => ({ ...prev, taxVnd, taxUsd }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.taxPercent, lastModifiedField]);

  // Fetch current balance (all transactions) - runs asynchronously
  const fetchCurrentBalance = async () => {
    setBalanceLoading(true);
    
    try {
      const params: any = {
        // Only filter by the currently selected account type
        accountType: formData.accountType
      };
      
      const response = await recordService.getCurrentBalance(params);
      
      const balanceData = response.data;
      setCurrentBalance({
        total_vnd: balanceData.total_vnd || 0,
        total_usd: balanceData.total_usd || 0
      });
      
      // Update cache status indicators
      setBalanceFromCache(balanceData.cached || false);
      setBalanceFresh(balanceData.is_fresh !== false); // Default to true if not specified
      
    } catch (error) {
      console.error('Error fetching current balance:', error);
      // Don't show error notification for balance - it's not critical for page functionality
    } finally {
      setBalanceLoading(false);
    }
  };

  // Force refresh balance (bypass cache)
  const forceRefreshBalance = async () => {
    try {
      setBalanceLoading(true);
      
      // Queue balance recalculation
      await cacheService.recalculateBalance(formData.accountType);
      
      // Wait a moment for the calculation to complete
      setTimeout(async () => {
        await fetchCurrentBalance();
        showNotification('Balance refreshed successfully', 'success');
      }, 1000);
      
    } catch (error) {
      console.error('Error refreshing balance:', error);
      showNotification('Error refreshing balance', 'error');
      setBalanceLoading(false);
    }
  };

  const fetchCategorySummary = async (customFilters?: any, recordsData?: FinancialRecord[]) => {
    setSummaryLoading(true);
    const filtersToUse = customFilters || filters;
    // Use provided records data or fall back to the current records state
    const recordsToUse = recordsData || records;
    
    try {
      // Calculate category summary directly from the records array
      // This ensures the numbers match exactly with what's shown in the records grid
      const categorySummaryMap = new Map();
      
      // If records array is empty, don't attempt to filter or calculate summary
      if (recordsToUse.length === 0) {
        setCategorySummary([]);
        setSummaryLoading(false);
        return;
      }
      
      // Apply filters to records before calculating summary
      const filteredRecords = recordsToUse.filter(record => {
        // Filter by date range if specified
        if (filtersToUse.startDate) {
          const recordDate = new Date(record.record_date);
          const startDate = new Date(filtersToUse.startDate);
          startDate.setHours(0, 0, 0, 0);
          
          if (recordDate < startDate) {
            return false;
          }
        }
        
        if (filtersToUse.endDate) {
          const recordDate = new Date(record.record_date);
          const endDate = new Date(filtersToUse.endDate);
          endDate.setHours(23, 59, 59, 999);
          
          if (recordDate > endDate) {
            return false;
          }
        }
        
        // Filter by record type if specified
        if (filtersToUse.recordType && filtersToUse.recordType !== '' && record.record_type !== filtersToUse.recordType) {
          return false;
        }
        
        // Filter by category if specified
        if (filtersToUse.categoryId && filtersToUse.categoryId.length > 0 && 
            !filtersToUse.categoryId.includes(record.category_id)) {
          return false;
        }
        
        // Filter by search term if specified
        if (filtersToUse.search && filtersToUse.search.trim() !== '') {
          const searchTerm = filtersToUse.search.toLowerCase();
          const description = record.description ? record.description.toLowerCase() : '';
          const categoryName = record.Category?.name ? record.Category.name.toLowerCase() : '';
          
          if (!description.includes(searchTerm) && !categoryName.includes(searchTerm)) {
            return false;
          }
        }
        
        // Filter by amount if specified
        if (filtersToUse.minAmount && filtersToUse.minAmount.trim() !== '') {
          const minAmount = parseFloat(filtersToUse.minAmount.replace(/,/g, ''));
          const recordAmount = filtersToUse.amountCurrency === 'usd' ? 
            record.amount_usd : record.amount_vnd;
          
          if (!isNaN(minAmount) && recordAmount < minAmount) {
            return false;
          }
        }
        
        if (filtersToUse.maxAmount && filtersToUse.maxAmount.trim() !== '') {
          const maxAmount = parseFloat(filtersToUse.maxAmount.replace(/,/g, ''));
          const recordAmount = filtersToUse.amountCurrency === 'usd' ? 
            record.amount_usd : record.amount_vnd;
          
          if (!isNaN(maxAmount) && recordAmount > maxAmount) {
            return false;
          }
        }
        
        return true;
      });
      
      // Group filtered records by category and record type
      filteredRecords.forEach(record => {
        const categoryId = record.category_id;
        const recordType = record.record_type;
        const key = `${categoryId}-${recordType}`;
        
        if (!categorySummaryMap.has(key)) {
          categorySummaryMap.set(key, {
            category_id: categoryId,
            record_type: recordType,
            total_amount: 0,
            total_amount_vnd: 0,
            total_amount_usd: 0,
            total_tax: 0,
            total_tax_vnd: 0,
            total_tax_usd: 0,
            total_report_vnd: 0, // Add report amount fields
            total_report_usd: 0,
            record_count: 0,
            Category: record.Category
          });
        }
        
        const summary = categorySummaryMap.get(key);
        summary.total_amount_vnd += Math.abs(record.amount_vnd);
        summary.total_amount_usd += Math.abs(record.amount_usd);
        
        // For income items, tax should be negative
        if (record.record_type === 'income') {
          summary.total_tax_vnd -= Math.abs(record.tax_amount_vnd);
          summary.total_tax_usd -= Math.abs(record.tax_amount_usd);
        } else {
          summary.total_tax_vnd += Math.abs(record.tax_amount_vnd);
          summary.total_tax_usd += Math.abs(record.tax_amount_usd);
        }
        
        // Calculate report amounts (amount + tax) - simple calculation as requested
        summary.total_report_vnd = summary.total_amount_vnd + summary.total_tax_vnd;
        summary.total_report_usd = summary.total_amount_usd + summary.total_tax_usd;
        summary.record_count += 1;
      });
      
      // Convert map to array
      const summaryData = Array.from(categorySummaryMap.values());
      setCategorySummary(summaryData);
    } catch (error) {
      console.error('Error calculating category summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchRecords = async (customFilters?: any) => {
    setLoading(true);
    const filtersToUse = customFilters || filters;
    
    try {
      const categoryIdParam = filtersToUse.categoryId && filtersToUse.categoryId.length > 0 
        ? filtersToUse.categoryId.join(',') 
        : '';
      

      
      const params: any = {
        sortBy: filtersToUse.sortBy,
        sortOrder: filtersToUse.sortOrder,
        // Always filter by the currently selected account type
        accountType: formData.accountType,
        // Always include all filter parameters, even if empty
        categoryId: categoryIdParam,
        recordType: filtersToUse.recordType || '',
        search: filtersToUse.search || '',
        // Add timestamp to prevent caching issues
        _t: Date.now()
      };

      // Add date filters if present
      if (filtersToUse.startDate) params.startDate = format(filtersToUse.startDate, 'yyyy-MM-dd');
      if (filtersToUse.endDate) params.endDate = format(filtersToUse.endDate, 'yyyy-MM-dd');
      
      // Add amount filters if present
      if (filtersToUse.minAmount && filtersToUse.minAmount.trim() !== '') {
        const parsedMinAmount = parseFloat(filtersToUse.minAmount.replace(/,/g, ''));
        if (!isNaN(parsedMinAmount)) {
          params.minAmount = parsedMinAmount;
        }
      }
      if (filtersToUse.maxAmount && filtersToUse.maxAmount.trim() !== '') {
        const parsedMaxAmount = parseFloat(filtersToUse.maxAmount.replace(/,/g, ''));
        if (!isNaN(parsedMaxAmount)) {
          params.maxAmount = parsedMaxAmount;
        }
      }
      
      // Always include currency for amount filtering
      if ((filtersToUse.minAmount && filtersToUse.minAmount.trim() !== '') || 
          (filtersToUse.maxAmount && filtersToUse.maxAmount.trim() !== '')) {
        params.amountCurrency = filtersToUse.amountCurrency;
      }

      const response = await recordService.getAll(params);
      const fetchedRecords = response.data.records;
      setRecords(fetchedRecords);
      setTotals(response.data.totals);
      
      // Also fetch category summary with the same filters and pass the fetched records
      await fetchCategorySummary(filtersToUse, fetchedRecords);
    } catch (error) {
      console.error('Error fetching records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveExchangeRate = async () => {
    try {
      const rate = parseFloat(newExchangeRate.replace(/,/g, ''));
      
      if (isNaN(rate) || rate <= 0) {
        showNotification('Please enter a valid exchange rate', 'warning');
        return;
      }

      // Check if the value actually changed
      const currentRateValue = currentRate ? currentRate.vnd_to_usd : 0;
      if (Math.abs(rate - currentRateValue) < 0.000001) {
        showNotification('Exchange rate value has not changed', 'warning');
        return;
      }
      
      // If we are editing a record, show warning about updating exchange rate
      if (editingRecord && editingExchangeRate && editingExchangeRate.id) {
        // Store the pending rate and show the warning modal
        setPendingExchangeRate(rate);
        setShowExchangeRateWarning(true);
      } else {
        // Create new exchange rate when not editing a record
        const createResponse = await exchangeRateService.create({ vnd_to_usd: rate });
        
        // Fetch updated exchange rate
        const response = await exchangeRateService.getCurrent();
        setCurrentRate(response.data);
        if (response.data) {
          setNewExchangeRate(formatNumber(response.data.vnd_to_usd));
        }
        
        showNotification('Exchange rate updated successfully', 'success');
      }
    } catch (error) {
      console.error('Error saving exchange rate:', error);
      showNotification('Error saving exchange rate', 'error');
    }
  };

  const handleExchangeRateUpdateOption = async (option: 'update-rate-only' | 'update-records' | 'cancel') => {
    if (!pendingExchangeRate || !editingExchangeRate || !editingExchangeRate.id) {
      setShowExchangeRateWarning(false);
      return;
    }
    
    try {
      const rate = pendingExchangeRate;
      
      if (option === 'cancel') {
        // Just close the modal and do nothing
        setShowExchangeRateWarning(false);
        setPendingExchangeRate(null);
        return;
      }
      
      if (option === 'update-rate-only') {
        // Update the exchange rate without affecting existing records
        const updateResponse = await exchangeRateService.update(editingExchangeRate.id, { vnd_to_usd: rate });
        
        // Update the editingExchangeRate directly
        const updatedRate = { ...editingExchangeRate, vnd_to_usd: rate };
        setEditingExchangeRate(updatedRate);
        setCurrentRate(updatedRate);
        setNewExchangeRate(formatNumber(rate));
        
        showNotification('Exchange rate updated successfully', 'success');
      } else if (option === 'update-records') {
        // Update exchange rate AND recalculate all related records
        const updateResponse = await exchangeRateService.update(editingExchangeRate.id, { 
          vnd_to_usd: rate,
          recalculate_records: true  // Special flag to recalculate related records
        });
        
        // Update the editingExchangeRate directly
        const updatedRate = { ...editingExchangeRate, vnd_to_usd: rate };
        setEditingExchangeRate(updatedRate);
        setCurrentRate(updatedRate);
        setNewExchangeRate(formatNumber(rate));
        
        // Refresh the records to show updated values
        await fetchRecords();
        
        showNotification('Exchange rate and related records updated successfully', 'success');
      }
      
      // Close the warning modal
      setShowExchangeRateWarning(false);
      setPendingExchangeRate(null);
      
    } catch (error) {
      console.error('Error updating exchange rate:', error);
      showNotification('Error updating exchange rate', 'error');
      setShowExchangeRateWarning(false);
      setPendingExchangeRate(null);
    }
  };

  const handleFilterChange = (newFilters: any) => {
    setFilters({ ...filters, ...newFilters });
  };

  const handleSearch = () => {
    fetchRecords();
  };

  const handleResetFilters = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const resetFilters = {
      startDate: startOfMonth,
      endDate: endOfMonth,
      recordType: '' as RecordType | '',
      accountType: '' as AccountType | '',
      categoryId: [] as number[], // Changed to array for multiple selection
      search: '',
      sortBy: 'record_date',
      sortOrder: 'desc',
      minAmount: '',
      maxAmount: '',
      amountCurrency: 'vnd' as 'vnd' | 'usd'
    };
    
    setFilters(resetFilters);
    
    // Fetch records immediately with the reset filters
    fetchRecords(resetFilters);
  };

  // Handle column header click for sorting
  const handleSort = (column: string) => {
    const newSortOrder = filters.sortBy === column && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    const newFilters = {
      ...filters,
      sortBy: column,
      sortOrder: newSortOrder
    };
    setFilters(newFilters);
    fetchRecords(newFilters);
  };

  // Get sort indicator for column headers
  const getSortIndicator = (column: string) => {
    if (filters.sortBy !== column) return ' ⇅';
    return filters.sortOrder === 'asc' ? ' ▲' : ' ▼';
  };

  // Get available months with data (last 12 months)
  const getAvailableMonths = () => {
    const months = [];
    const now = new Date();
    
    // Generate last 12 months
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // 1-indexed for display
      const monthStr = `${year}-${month.toString().padStart(2, '0')}`;
      const displayStr = `${month.toString().padStart(2, '0')}/${year}`;
      months.push({ value: monthStr, label: displayStr });
    }
    
    return months;
  };

  // Handle copy records from previous month - show dialog
  const handleCopyFromPreviousMonth = () => {
    // Set default values: fromMonth = last month, toMonth = current month
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 1-indexed
    const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const lastMonthYear = currentMonth === 1 ? currentYear - 1 : currentYear;
    
    const defaultFromMonth = `${lastMonthYear}-${lastMonth.toString().padStart(2, '0')}`;
    const defaultToMonth = `${currentYear}-${currentMonth.toString().padStart(2, '0')}`;
    
    setCopyFromMonth(defaultFromMonth);
    setCopyToMonth(defaultToMonth);
    setShowCopyDialog(true);
    setCopyOption('expense-only'); // Default to expense only
  };

  // Handle copy dialog close
  const handleCopyDialogClose = () => {
    setShowCopyDialog(false);
  };

  // Handle copy dialog confirm
  const handleCopyDialogConfirm = async () => {
    setShowCopyDialog(false);
    
    setCopyLoading(true);
    
    try {
      const response = await recordService.copyFromPreviousMonth(formData.accountType, copyOption, copyFromMonth, copyToMonth);
      
      if (response.data.success) {
        const { copiedCount, skippedCount, details, copiedRecordIds } = response.data;
        
        let message = `✅ Copy completed!\n\n`;
        message += `📋 ${copiedCount} records copied\n`;
        if (skippedCount > 0) {
          message += `⏭️ ${skippedCount} records skipped (already exist)\n`;
        }
        message += `\n📅 From: ${details.previousMonth}\n`;
        message += `📅 To: ${details.currentMonth}\n`;
        message += `💱 Exchange Rate: ${details.exchangeRate.toLocaleString()}`;
        
        showNotification(message, 'success');
        
        // Refresh records and balance
        await fetchRecords();
        fetchCurrentBalance();
        
        // Highlight the copied records using the returned IDs
        if (copiedRecordIds && copiedRecordIds.length > 0) {
          setCopiedRecordIds(new Set(copiedRecordIds));
          // Automatically clear highlighting after 10 seconds
          setTimeout(() => {
            setCopiedRecordIds(new Set());
          }, 120000);
        }
        
      } else {
        showNotification(response.data.message || 'Copy operation failed', 'error');
      }
    } catch (error: any) {
      console.error('Error copying records from previous month:', error);
      
      const errorMessage = error.response?.data?.message || error.message || 'Error copying records from previous month';
      showNotification(errorMessage, 'error');
    } finally {
      setCopyLoading(false);
    }
  };

  // Clear copied record highlighting
  const clearCopiedHighlighting = () => {
    setCopiedRecordIds(new Set());
  };

  // Handle import customer payments dialog
  const handleImportCustomerPayments = () => {
    setShowImportDialog(true);
    setImportData('');
    setParsedImportData([]);
    // Set default exchange rate to current rate
    if (currentRate) {
      const defaultRate = Math.round(currentRate.vnd_to_usd);
      setImportExchangeRate(defaultRate.toString());
      setImportExchangeRateId(currentRate.id);
      setAppliedExchangeRate(defaultRate);
    }
  };

  // Get closest exchange rate to a specific date
  const getClosestExchangeRate = async (targetDate: Date): Promise<number> => {
    try {
      // Get exchange rate history and find the closest one to the target date
      const response = await exchangeRateService.getHistory({ 
        limit: 100, // Get recent rates
        sortBy: 'effective_date',
        sortOrder: 'desc'
      });
      
      if (response.data && Array.isArray(response.data)) {
        const rates = response.data;
        const targetTime = targetDate.getTime();
        
        // Find the rate with effective_date closest to but not after the target date
        let closestRate = null;
        let closestDiff = Infinity;
        
        for (const rate of rates) {
          const rateDate = new Date(rate.effective_date);
          const rateDiff = targetTime - rateDate.getTime();
          
          // Only consider rates that are on or before the target date
          if (rateDiff >= 0 && rateDiff < closestDiff) {
            closestDiff = rateDiff;
            closestRate = rate;
          }
        }
        
        if (closestRate && closestRate.vnd_to_usd) {
          return closestRate.vnd_to_usd;
        }
      }
    } catch (error) {
      // Silent fallback to current rate
    }
    
    // Fallback to current exchange rate
    return currentRate?.vnd_to_usd || 25000;
  };

  // Parse Excel data from textarea
  const parseExcelData = async (data: string) => {
    const lines = data.trim().split('\n');
    const parsedRecords = [];
    const duplicates = [];
    
    // Track duplicates within the import data itself
    const importDataMap = new Map();
    
    // Debug the input data
    console.log("Input data:", data);
    console.log("Lines:", lines.length);
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;
      
      // Handle both tab-delimited and space-padded data
      // First try tab delimiter
      let columns = line.split('\t');
      
      // If we don't have enough columns, try to detect and fix the format
      if (columns.length < 5) {
        console.log("Line format issue:", line);
        // Try to detect date format DD/MM/YYYY and split accordingly
        const dateMatch = line.match(/(\d{2}\/\d{2}\/\d{4})/g);
        if (dateMatch && dateMatch.length >= 1) {
          // We have a date, try to parse more intelligently
          const parts = line.split(/\s{2,}/); // Split by 2 or more spaces
          columns = parts.filter(p => p.trim());
          console.log("Reformatted columns:", columns);
        }
      }
      
      try {
        const dateStr = columns[0]?.trim();
        const timeStr = columns[1]?.trim();
        const recordId = columns[2]?.trim(); // We ignore this
        const description = columns[3]?.trim();
        // The amount might be in column 4 or the last column
        const beforeTaxStr = columns[4]?.trim() || columns[columns.length - 1]?.trim();
        const afterTaxStr = columns[5]?.trim() || ''; // Optional
        const taxAmountStr = columns[6]?.trim() || ''; // Optional
        
        console.log("Parsing row:", { dateStr, timeStr, recordId, description, beforeTaxStr });
        
        if (!dateStr || !description) {
          console.log("Missing required fields:", { dateStr, description });
          continue;
        }
        
        // If amount is missing but we have a number at the end, use that
        if (!beforeTaxStr && columns.length > 4) {
          const lastColumn = columns[columns.length - 1]?.trim();
          if (lastColumn && /[\d,.]+/.test(lastColumn)) {
            console.log("Using last column as amount:", lastColumn);
          }
        }
        
        // Parse date - handle VN format DD/MM/YYYY
        let recordDate;
        if (dateStr.includes('/')) {
          const dateParts = dateStr.split('/');
          if (dateParts.length === 3) {
            const day = parseInt(dateParts[0]);
            const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed in JavaScript Date
            const year = parseInt(dateParts[2]);
            
            // Create date in UTC to avoid timezone issues
            // This ensures the date is exactly what was entered without timezone shifting
            recordDate = new Date(Date.UTC(year, month, day));
            
            // Format as YYYY-MM-DD to ensure consistency
            const isoDate = recordDate.toISOString().split('T')[0];
            console.log(`Date conversion: ${dateStr} -> ${isoDate}`);
          } else {
            recordDate = new Date(dateStr);
          }
        } else {
          recordDate = new Date(dateStr);
        }
        
        if (isNaN(recordDate.getTime())) {
          console.log("Invalid date:", dateStr);
          continue;
        }
        
        // Parse amounts - remove commas and non-numeric characters except dots and minus
        let beforeTax = 0;
        
        if (beforeTaxStr) {
          // Handle different number formats (e.g., 4,620,000.00 or 4.620.000,00)
          const cleanedStr = beforeTaxStr.replace(/[^\d,.]/g, '');
          
          // Determine if we have European or US number format
          if (cleanedStr.includes(',') && cleanedStr.includes('.')) {
            // If both comma and dot are present, check which is the decimal separator
            const lastCommaIndex = cleanedStr.lastIndexOf(',');
            const lastDotIndex = cleanedStr.lastIndexOf('.');
            
            if (lastCommaIndex > lastDotIndex) {
              // European format: 1.234,56
              const europeanStr = cleanedStr.replace(/\./g, '').replace(',', '.');
              beforeTax = parseFloat(europeanStr) || 0;
            } else {
              // US format: 1,234.56
              beforeTax = parseFloat(cleanedStr.replace(/,/g, '')) || 0;
            }
          } else if (cleanedStr.includes(',')) {
            // If only comma is present, check if it's a decimal separator or thousands separator
            if (cleanedStr.length - cleanedStr.lastIndexOf(',') <= 3) {
              // Likely decimal separator: 1234,56
              beforeTax = parseFloat(cleanedStr.replace(',', '.')) || 0;
            } else {
              // Likely thousands separator: 1,234
              beforeTax = parseFloat(cleanedStr.replace(/,/g, '')) || 0;
            }
          } else {
            // Standard case or only dots
            beforeTax = parseFloat(cleanedStr.replace(/,/g, '')) || 0;
          }
        }
        
        console.log("Parsed amount:", beforeTax);
        
        let afterTax = 0;
        let taxAmount = 0;
        let taxPercent = 10; // Default 10% if tax columns are missing
        
        if (afterTaxStr && taxAmountStr) {
          // Both tax columns provided
          afterTax = parseFloat(afterTaxStr.replace(/[^0-9.-]/g, '')) || 0;
          taxAmount = parseFloat(taxAmountStr.replace(/[^0-9.-]/g, '')) || 0;
          
          // For income records: beforeTax is the GROSS amount (includes tax)
          // Calculate tax percentage using income formula: tax% = (taxAmount / (beforeTax - taxAmount)) * 100
          // Or reverse: taxAmount = gross - (gross / (1 + tax%))
          if (taxAmount > 0 && beforeTax > 0) {
            const netAmount = beforeTax - taxAmount;
            taxPercent = (taxAmount / netAmount) * 100;
          }
        } else {
          // Missing tax columns, use 10% default
          // For income: beforeTax is gross, calculate tax using income formula
          taxPercent = 10;
          const taxDecimal = taxPercent / 100;
          taxAmount = Math.round(beforeTax - (beforeTax / (1 + taxDecimal)));
          afterTax = beforeTax + taxAmount;
        }
        
        if (beforeTax <= 0) {
          console.log("Skipping record due to invalid amount:", beforeTax);
          continue;
        }
        
        
        // Use import exchange rate (already set when dialog opens)
        const exchangeRate = parseFloat(importExchangeRate) || currentRate?.vnd_to_usd || 25000;
        
        // Calculate USD amounts
        const amountUsd = beforeTax / exchangeRate;
        const taxAmountUsd = taxAmount / exchangeRate;
        
        // Extract time from datetime string or use default
        let recordTime = '00:00:00';
        if (timeStr && timeStr.includes(' ')) {
          const timePart = timeStr.split(' ')[1];
          if (timePart) recordTime = timePart;
        }
        
        // Ensure we have the correct date format (YYYY-MM-DD) without timezone issues
        const formattedDate = recordDate.toISOString().split('T')[0];
        
        // For income records: amount is positive, tax is negative, report_amount = amount + tax_amount
        const record = {
          record_date: formattedDate,
          record_time: recordTime,
          description: description,
          amount_vnd: beforeTax,
          amount_usd: parseFloat(amountUsd.toFixed(2)),
          tax_percent: parseFloat(taxPercent.toFixed(2)),
          tax_amount_vnd: -Math.abs(taxAmount), // Negative for income
          tax_amount_usd: -Math.abs(parseFloat(taxAmountUsd.toFixed(2))), // Negative for income
          record_type: 'income' as RecordType,
          account_type: formData.accountType,
          exchange_rate_id: importExchangeRateId || currentRate?.id || null,
          category_id: 11, // Default to VN Customer category
          isDuplicate: false
        };
        
        // Check for duplicates within import data itself
        const recordKey = `${description.toLowerCase()}_${record.record_date}`;
        
        if (importDataMap.has(recordKey)) {
          // Mark as duplicate if it appears multiple times in the import data
          record.isDuplicate = true;
          duplicates.push(record);
        } else {
          // Add to import data map to track duplicates within the import
          importDataMap.set(recordKey, record);
        }
        
        parsedRecords.push(record);
      } catch (error) {
        console.error(`Error parsing line ${i + 1}:`, line, error);
      }
    }
    
    // Set the duplicates state
    setDuplicateRecords(duplicates);
    
    return parsedRecords;
  };

  // Handle import data processing
  const handleImportProcess = async () => {
    if (!importData.trim()) {
      showNotification('Please paste data to import', 'warning');
      return;
    }
    
    const rateValue = parseFloat(importExchangeRate);
    if (!rateValue || rateValue <= 0) {
      showNotification('Please enter a valid exchange rate', 'warning');
      return;
    }
    
    setImportLoading(true);
    setDuplicateRecords([]);
    
    try {
      // Don't find/create exchange rate yet - just parse with the rate value
      // Exchange rate will be found/created when user clicks Apply or Import
      
      // Parse the data from the text area
      const parsed = await parseExcelData(importData);
      
      if (parsed.length === 0) {
        showNotification('No valid records found in the data', 'warning');
        setImportLoading(false);
        return;
      }
      
      // Now check with the backend for duplicates
      const duplicateCheckResponse = await recordService.checkDuplicates(parsed);
      const checkedRecords = duplicateCheckResponse.data.results;
      
      // Update the isDuplicate flag based on backend response
      const updatedRecords = parsed.map((record, index) => {
        if (index < checkedRecords.length && checkedRecords[index].isDuplicate) {
          return { ...record, isDuplicate: true, duplicateId: checkedRecords[index].duplicateId };
        }
        return record;
      });
      
      // Update duplicate records array
      const serverDuplicates = updatedRecords.filter(r => r.isDuplicate);
      setDuplicateRecords(serverDuplicates);
      
      setParsedImportData(updatedRecords);
      
      const duplicateCount = serverDuplicates.length;
      const uniqueCount = updatedRecords.length - duplicateCount;
      
      if (duplicateCount > 0) {
        showNotification(`Parsed ${updatedRecords.length} records (${uniqueCount} unique, ${duplicateCount} duplicates)`, 'warning');
      } else {
        showNotification(`Parsed ${updatedRecords.length} records successfully`, 'success');
      }
    } catch (error) {
      console.error('Error parsing import data:', error);
      showNotification('Error parsing import data', 'error');
    } finally {
      setImportLoading(false);
    }
  };

  // Handle import confirmation
  const handleImportConfirm = async () => {
    if (parsedImportData.length === 0) {
      showNotification('No data to import', 'warning');
      return;
    }
    
    setImportLoading(true);
    
    try {
      // Ensure exchange rate is found/created before importing
      // This handles the case where user didn't click Apply button
      const rateValue = parseFloat(importExchangeRate);
      if (!rateValue || rateValue <= 0) {
        showNotification('Please enter a valid exchange rate', 'warning');
        setImportLoading(false);
        return;
      }
      
      // Only find/create if not already set (i.e., user didn't click Apply)
      let finalExchangeRateId = importExchangeRateId;
      if (!finalExchangeRateId) {
        finalExchangeRateId = await findOrCreateExchangeRate(rateValue);
        setImportExchangeRateId(finalExchangeRateId);
        
        // Update all parsed records with the exchange rate ID
        const updatedRecords = parsedImportData.map(record => ({
          ...record,
          exchange_rate_id: finalExchangeRateId
        }));
        setParsedImportData(updatedRecords);
      }
      
      let successCount = 0;
      let errorCount = 0;
      let skippedCount = 0;
      let overriddenCount = 0;
      
      for (const record of parsedImportData) {
        // Handle duplicates based on user's choice
        if (record.isDuplicate) {
          if (duplicateHandling === 'skip') {
            skippedCount++;
            continue;
          } else if (duplicateHandling === 'override' && record.duplicateId) {
            // Override: delete the existing record and create new one
            try {
              await recordService.delete(record.duplicateId);
              const { isDuplicate, duplicateId, ...recordToSave } = record;
              await recordService.create(recordToSave);
              overriddenCount++;
              successCount++;
            } catch (error) {
              console.error('Error overriding duplicate record:', error);
              errorCount++;
            }
            continue;
          }
        }
        
        try {
          // Remove the isDuplicate flag before sending to the server
          const { isDuplicate, duplicateId, ...recordToSave } = record;
          await recordService.create(recordToSave);
          successCount++;
        } catch (error) {
          console.error('Error importing record:', error);
          errorCount++;
        }
      }
      
      if (successCount > 0 || skippedCount > 0) {
        let message = `Successfully imported ${successCount} records`;
        if (overriddenCount > 0) message += ` (${overriddenCount} duplicates overridden)`;
        if (skippedCount > 0) message += ` (${skippedCount} duplicates skipped)`;
        if (errorCount > 0) message += ` (${errorCount} failed)`;
        
        showNotification(message, 'success');
        
        // Refresh data
        await fetchRecords();
        fetchCurrentBalance();
        
        // Close dialog
        setShowImportDialog(false);
        setImportData('');
        setParsedImportData([]);
        setDuplicateRecords([]);
        setDuplicateHandling('skip'); // Reset to default
        setImportExchangeRate('');
        setImportExchangeRateId(null);
        setAppliedExchangeRate(null);
      } else if (skippedCount > 0 && successCount === 0) {
        showNotification(`All ${skippedCount} records were duplicates and skipped`, 'warning');
      } else {
        showNotification('Failed to import any records', 'error');
      }
    } catch (error) {
      console.error('Error during import:', error);
      showNotification('Error during import process', 'error');
    } finally {
      setImportLoading(false);
    }
  };

  // Find or create exchange rate
  const findOrCreateExchangeRate = async (rateValue: number): Promise<number> => {
    try {
      // Get all exchange rates to find any matching value
      const response = await exchangeRateService.getHistory({ 
        limit: 1000, // Get a large number to search through all rates
        sortBy: 'effective_date',
        sortOrder: 'desc'
      });
      
      const rates = response.data.rates || [];
      
      // Find all rates that match the value (rounded)
      const matchingRates = rates.filter((rate: any) => {
        const roundedRate = Math.round(rate.vnd_to_usd);
        return roundedRate === rateValue;
      });
      
      if (matchingRates.length > 0) {
        // Use the most recent matching rate (first one since sorted by date desc)
        const mostRecentMatch = matchingRates[0];
        console.log('Found existing exchange rate:', mostRecentMatch.id, 'with value:', mostRecentMatch.vnd_to_usd, 'from date:', mostRecentMatch.effective_date);
        return mostRecentMatch.id;
      }
      
      // No matching rate found in the entire history, create a new one
      console.log('No existing rate found for value:', rateValue, '- creating new exchange rate');
      const newRate = await exchangeRateService.create({
        effective_date: new Date().toISOString().split('T')[0],
        vnd_to_usd: rateValue
      });
      
      console.log('Created new exchange rate:', newRate.data.id);
      return newRate.data.id;
    } catch (error) {
      console.error('Error finding/creating exchange rate:', error);
      throw error;
    }
  };
  
  // Apply exchange rate to parsed records
  const handleApplyExchangeRate = async () => {
    const rateValue = parseFloat(importExchangeRate);
    
    if (!rateValue || rateValue <= 0) {
      showNotification('Please enter a valid exchange rate', 'warning');
      return;
    }
    
    if (parsedImportData.length === 0) {
      showNotification('No records to apply exchange rate to', 'warning');
      return;
    }
    
    setImportLoading(true);
    
    try {
      // Find or create the exchange rate
      const exchangeRateId = await findOrCreateExchangeRate(rateValue);
      setImportExchangeRateId(exchangeRateId);
      
      // Recalculate all parsed records with new exchange rate
      const updatedRecords = parsedImportData.map(record => {
        const amountUsd = parseFloat((record.amount_vnd / rateValue).toFixed(2));
        const taxAmountUsd = -Math.abs(parseFloat((Math.abs(record.tax_amount_vnd) / rateValue).toFixed(2)));
        
        return {
          ...record,
          amount_usd: amountUsd,
          tax_amount_usd: taxAmountUsd,
          exchange_rate_id: exchangeRateId
        };
      });
      
      setParsedImportData(updatedRecords);
      setAppliedExchangeRate(rateValue); // Update the applied rate for preview display
      showNotification('Exchange rate applied successfully', 'success');
    } catch (error) {
      console.error('Error applying exchange rate:', error);
      showNotification('Error applying exchange rate', 'error');
    } finally {
      setImportLoading(false);
    }
  };

  // Handle import dialog close
  const handleImportDialogClose = () => {
    setShowImportDialog(false);
    setImportData('');
    setParsedImportData([]);
    setDuplicateRecords([]);
    setDuplicateHandling('skip'); // Reset to default
    setImportExchangeRate('');
    setImportExchangeRateId(null);
    setAppliedExchangeRate(null);
  };

  // ============ EXPENSE IMPORT FUNCTIONS ============

  // Handle import expense payments dialog
  const handleImportExpensePayments = () => {
    setShowExpenseImportDialog(true);
    setExpenseImportData('');
    setParsedExpenseImportData([]);
    // Set default exchange rate to current rate
    if (currentRate) {
      const defaultRate = Math.round(currentRate.vnd_to_usd);
      setExpenseImportExchangeRate(defaultRate.toString());
      setExpenseImportExchangeRateId(currentRate.id);
      setExpenseAppliedExchangeRate(defaultRate);
    }
  };

  // Parse expense Excel data from textarea (amount is in 5th column instead of 6th)
  const parseExpenseData = async (data: string) => {
    const lines = data.trim().split('\n');
    const parsedRecords = [];
    const duplicates = [];
    
    // Track duplicates within the import data itself
    const importDataMap = new Map();
    
    console.log("Parsing expense data:", data);
    console.log("Lines:", lines.length);
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;
      
      // Handle both tab-delimited and space-padded data
      let columns = line.split('\t');
      
      // If we don't have enough columns, try to detect and fix the format
      if (columns.length < 5) {
        const dateMatch = line.match(/(\d{2}\/\d{2}\/\d{4})/g);
        if (dateMatch && dateMatch.length >= 1) {
          const parts = line.split(/\s{2,}/);
          columns = parts.filter(p => p.trim());
        }
      }
      
      try {
        const dateStr = columns[0]?.trim();
        const timeStr = columns[1]?.trim();
        const recordId = columns[2]?.trim(); // We ignore this
        const description = columns[3]?.trim();
        // For expense: amount is in 5th column (index 4)
        const amountStr = columns[4]?.trim();
        
        console.log("Parsing expense row:", { dateStr, timeStr, recordId, description, amountStr });
        
        if (!dateStr || !description) {
          console.log("Missing required fields:", { dateStr, description });
          continue;
        }
        
        // Parse date - handle VN format DD/MM/YYYY
        let recordDate;
        if (dateStr.includes('/')) {
          const dateParts = dateStr.split('/');
          if (dateParts.length === 3) {
            const day = parseInt(dateParts[0]);
            const month = parseInt(dateParts[1]) - 1;
            const year = parseInt(dateParts[2]);
            recordDate = new Date(Date.UTC(year, month, day));
            const isoDate = recordDate.toISOString().split('T')[0];
            console.log(`Date conversion: ${dateStr} -> ${isoDate}`);
          } else {
            recordDate = new Date(dateStr);
          }
        } else {
          recordDate = new Date(dateStr);
        }
        
        if (isNaN(recordDate.getTime())) {
          console.log("Invalid date:", dateStr);
          continue;
        }
        
        // Parse amount - remove commas and non-numeric characters except dots and minus
        let amount = 0;
        if (amountStr) {
          const cleanedStr = amountStr.replace(/[^\d,.]/g, '');
          
          // Determine if we have European or US number format
          if (cleanedStr.includes(',') && cleanedStr.includes('.')) {
            const lastCommaIndex = cleanedStr.lastIndexOf(',');
            const lastDotIndex = cleanedStr.lastIndexOf('.');
            
            if (lastCommaIndex > lastDotIndex) {
              // European format: 1.234,56
              const europeanStr = cleanedStr.replace(/\./g, '').replace(',', '.');
              amount = parseFloat(europeanStr) || 0;
            } else {
              // US format: 1,234.56
              amount = parseFloat(cleanedStr.replace(/,/g, '')) || 0;
            }
          } else if (cleanedStr.includes(',')) {
            if (cleanedStr.length - cleanedStr.lastIndexOf(',') <= 3) {
              amount = parseFloat(cleanedStr.replace(',', '.')) || 0;
            } else {
              amount = parseFloat(cleanedStr.replace(/,/g, '')) || 0;
            }
          } else {
            amount = parseFloat(cleanedStr.replace(/,/g, '')) || 0;
          }
        }
        
        console.log("Parsed expense amount:", amount);
        
        if (amount <= 0) {
          console.log("Skipping record due to invalid amount:", amount);
          continue;
        }
        
        // Default tax percent for expenses (can be edited inline)
        const taxPercent = 0; // Default to 0% for expenses
        
        // Use expense import exchange rate
        const exchangeRate = parseFloat(expenseImportExchangeRate) || currentRate?.vnd_to_usd || 25000;
        
        // Calculate USD amounts
        const amountUsd = amount / exchangeRate;
        
        // Extract time from datetime string or use default
        let recordTime = '00:00:00';
        if (timeStr && timeStr.includes(' ')) {
          const timePart = timeStr.split(' ')[1];
          if (timePart) recordTime = timePart;
        }
        
        // Ensure we have the correct date format (YYYY-MM-DD)
        const formattedDate = recordDate.toISOString().split('T')[0];
        
        // For expense records: amount is negative, tax is negative
        const record = {
          record_date: formattedDate,
          record_time: recordTime,
          description: description,
          amount_vnd: -Math.abs(amount), // Negative for expense
          amount_usd: -Math.abs(parseFloat(amountUsd.toFixed(2))), // Negative for expense
          tax_percent: taxPercent,
          tax_amount_vnd: 0, // Default to 0, can be edited
          tax_amount_usd: 0,
          record_type: 'expense' as RecordType,
          account_type: formData.accountType,
          exchange_rate_id: expenseImportExchangeRateId || currentRate?.id || null,
          category_id: 9, // Default to Service category
          isDuplicate: false,
          excluded: false // Track if user wants to exclude from import
        };
        
        // Check for duplicates within import data itself
        const recordKey = `${description.toLowerCase()}_${record.record_date}`;
        
        if (importDataMap.has(recordKey)) {
          record.isDuplicate = true;
          duplicates.push(record);
        } else {
          importDataMap.set(recordKey, record);
        }
        
        parsedRecords.push(record);
      } catch (error) {
        console.error(`Error parsing expense line ${i + 1}:`, line, error);
      }
    }
    
    setExpenseDuplicateRecords(duplicates);
    return parsedRecords;
  };

  // Handle expense import data processing
  const handleExpenseImportProcess = async () => {
    if (!expenseImportData.trim()) {
      showNotification('Please paste data to import', 'warning');
      return;
    }
    
    const rateValue = parseFloat(expenseImportExchangeRate);
    if (!rateValue || rateValue <= 0) {
      showNotification('Please enter a valid exchange rate', 'warning');
      return;
    }
    
    setExpenseImportLoading(true);
    setExpenseDuplicateRecords([]);
    
    try {
      // Parse the data from the text area
      const parsed = await parseExpenseData(expenseImportData);
      
      if (parsed.length === 0) {
        showNotification('No valid records found in the data', 'warning');
        setExpenseImportLoading(false);
        return;
      }
      
      // Check with the backend for duplicates
      const duplicateCheckResponse = await recordService.checkDuplicates(parsed);
      const checkedRecords = duplicateCheckResponse.data.results;
      
      // Update the isDuplicate flag based on backend response
      const updatedRecords = parsed.map((record, index) => {
        if (index < checkedRecords.length && checkedRecords[index].isDuplicate) {
          return { ...record, isDuplicate: true, duplicateId: checkedRecords[index].duplicateId };
        }
        return record;
      });
      
      // Update duplicate records array
      const serverDuplicates = updatedRecords.filter(r => r.isDuplicate);
      setExpenseDuplicateRecords(serverDuplicates);
      
      setParsedExpenseImportData(updatedRecords);
      
      const duplicateCount = serverDuplicates.length;
      const uniqueCount = updatedRecords.length - duplicateCount;
      
      showNotification(
        `Parsed ${updatedRecords.length} records (${uniqueCount} unique, ${duplicateCount} duplicates)`,
        duplicateCount > 0 ? 'warning' : 'success'
      );
    } catch (error: any) {
      console.error('Error processing expense import data:', error);
      showNotification(error.response?.data?.message || 'Error processing data', 'error');
    } finally {
      setExpenseImportLoading(false);
    }
  };

  // Handle expense import confirmation
  const handleExpenseImportConfirm = async () => {
    if (parsedExpenseImportData.length === 0) {
      showNotification('No data to import', 'warning');
      return;
    }
    
    // Filter out excluded records
    const recordsToImport = parsedExpenseImportData.filter(r => !r.excluded);
    
    if (recordsToImport.length === 0) {
      showNotification('All records are excluded. Nothing to import.', 'warning');
      return;
    }
    
    // Validate that all non-excluded records have a category selected
    const recordsWithoutCategory = recordsToImport.filter(r => !r.category_id);
    if (recordsWithoutCategory.length > 0) {
      showNotification(`Please select a category for all ${recordsWithoutCategory.length} non-excluded record(s)`, 'warning');
      return;
    }
    
    setExpenseImportLoading(true);
    
    try {
      // Find or create the exchange rate if needed
      if (!expenseImportExchangeRateId) {
        const rateValue = parseFloat(expenseImportExchangeRate);
        const exchangeRateId = await findOrCreateExchangeRate(rateValue);
        setExpenseImportExchangeRateId(exchangeRateId);
        
        // Update all parsed records with the exchange rate ID
        const updatedRecords = parsedExpenseImportData.map(record => ({
          ...record,
          exchange_rate_id: exchangeRateId
        }));
        setParsedExpenseImportData(updatedRecords);
      }
      
      let successCount = 0;
      let errorCount = 0;
      let skippedCount = 0;
      let overriddenCount = 0;
      let excludedCount = parsedExpenseImportData.length - recordsToImport.length;
      
      for (const record of recordsToImport) {
        // Handle duplicates based on user's choice
        if (record.isDuplicate) {
          if (expenseDuplicateHandling === 'skip') {
            skippedCount++;
            continue;
          } else {
            // Override - delete the existing record first
            try {
              await recordService.delete(record.duplicateId);
              overriddenCount++;
            } catch (error) {
              console.error('Error deleting duplicate record:', error);
            }
          }
        }
        
        try {
          await recordService.create(record);
          successCount++;
        } catch (error) {
          console.error('Error creating expense record:', error);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        let message = `✅ Successfully imported ${successCount} expense record${successCount !== 1 ? 's' : ''}`;
        if (excludedCount > 0) {
          message += `\n⊘ Excluded ${excludedCount} record${excludedCount !== 1 ? 's' : ''}`;
        }
        if (skippedCount > 0) {
          message += `\n⏭️ Skipped ${skippedCount} duplicate${skippedCount !== 1 ? 's' : ''}`;
        }
        if (overriddenCount > 0) {
          message += `\n🔄 Overridden ${overriddenCount} duplicate${overriddenCount !== 1 ? 's' : ''}`;
        }
        if (errorCount > 0) {
          message += `\n❌ Failed to import ${errorCount} record${errorCount !== 1 ? 's' : ''}`;
        }
        
        showNotification(message, errorCount > 0 ? 'warning' : 'success');
        
        // Close dialog
        setShowExpenseImportDialog(false);
        setExpenseImportData('');
        setParsedExpenseImportData([]);
        setExpenseDuplicateRecords([]);
        setExpenseDuplicateHandling('skip');
        setExpenseImportExchangeRate('');
        setExpenseImportExchangeRateId(null);
        setExpenseAppliedExchangeRate(null);
        
        // Refresh records and balance
        await fetchRecords();
        fetchCurrentBalance();
      } else {
        showNotification('No records were imported', 'error');
      }
    } catch (error: any) {
      console.error('Error importing expense records:', error);
      showNotification(error.response?.data?.message || 'Error importing records', 'error');
    } finally {
      setExpenseImportLoading(false);
    }
  };

  // Handle apply expense exchange rate
  const handleApplyExpenseExchangeRate = async () => {
    const rateValue = parseFloat(expenseImportExchangeRate);
    if (!rateValue || rateValue <= 0) {
      showNotification('Please enter a valid exchange rate', 'warning');
      return;
    }
    
    if (parsedExpenseImportData.length === 0) {
      showNotification('No records to apply exchange rate to', 'warning');
      return;
    }
    
    setExpenseImportLoading(true);
    
    try {
      const exchangeRateId = await findOrCreateExchangeRate(rateValue);
      setExpenseImportExchangeRateId(exchangeRateId);
      
      // Recalculate all parsed records with new exchange rate
      const updatedRecords = parsedExpenseImportData.map(record => {
        const amountVnd = Math.abs(record.amount_vnd);
        const amountUsd = -Math.abs(parseFloat((amountVnd / rateValue).toFixed(2)));
        const taxAmountVnd = Math.abs(record.tax_amount_vnd);
        const taxAmountUsd = taxAmountVnd > 0 ? -Math.abs(parseFloat((taxAmountVnd / rateValue).toFixed(2))) : 0;
        
        return {
          ...record,
          amount_usd: amountUsd,
          tax_amount_usd: taxAmountUsd,
          exchange_rate_id: exchangeRateId
        };
      });
      
      setParsedExpenseImportData(updatedRecords);
      setExpenseAppliedExchangeRate(rateValue);
      showNotification('Exchange rate applied successfully', 'success');
    } catch (error) {
      console.error('Error applying expense exchange rate:', error);
      showNotification('Error applying exchange rate', 'error');
    } finally {
      setExpenseImportLoading(false);
    }
  };

  // Handle expense import dialog close
  const handleExpenseImportDialogClose = () => {
    setShowExpenseImportDialog(false);
    setExpenseImportData('');
    setParsedExpenseImportData([]);
    setExpenseDuplicateRecords([]);
    setExpenseDuplicateHandling('skip');
    setExpenseImportExchangeRate('');
    setExpenseImportExchangeRateId(null);
    setExpenseAppliedExchangeRate(null);
    setExpenseExcludedRecords(new Set());
    setShowExpenseTextarea(true);
  };

  // Handle inline edit of expense import record
  const handleExpenseRecordEdit = (index: number, field: string, value: any) => {
    const updatedRecords = [...parsedExpenseImportData];
    updatedRecords[index] = {
      ...updatedRecords[index],
      [field]: value
    };
    setParsedExpenseImportData(updatedRecords);
  };

  // This function is kept for future use when adding "New Record" button functionality
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCreateRecord = () => {
    setEditingRecord(null);
    // Reset form to default values
    setFormData({
      recordType: 'expense',
      accountType: 'company',
      date: new Date().toISOString().split('T')[0],
      description: '',
      amountVnd: '0',
      amountUsd: '0.0',
      taxPercent: '0',
      taxVnd: '0',
      taxUsd: '0.0',
      categoryId: ''
    });
    // Reset exchange rate to current rate if editing was in progress
    if (editingExchangeRate && originalExchangeRate) {
      setCurrentRate(originalExchangeRate);
      setNewExchangeRate(formatNumber(originalExchangeRate.vnd_to_usd));
      setOriginalExchangeRate(null);
      setEditingExchangeRate(null);
    }
    // Clear LLM state when creating new record
    clearLLMState();
    setDescriptionChanged(false); // Reset on new record
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    if (editingRecord) {
      // Reset form to default values
      setFormData({
        recordType: 'expense',
        accountType: 'company',
        date: new Date().toISOString().split('T')[0],
        description: '',
        amountVnd: '0',
        amountUsd: '0.0',
        taxPercent: '0',
        taxVnd: '0',
        taxUsd: '0.0',
        categoryId: ''
      });
      
      // Restore original exchange rate if editing was in progress
      if (editingExchangeRate && originalExchangeRate) {
        setCurrentRate(originalExchangeRate);
        setNewExchangeRate(formatNumber(originalExchangeRate.vnd_to_usd));
        setOriginalExchangeRate(null);
        setEditingExchangeRate(null);
      }
      
      // Clear editing state
      setEditingRecord(null);
      setLastModifiedField('');
      setPreviousFormValues({
        amountVnd: '0',
        amountUsd: '0.0',
        taxPercent: '0',
        taxVnd: '0',
        taxUsd: '0.0'
      });
      
      // Clear LLM state
      clearLLMState();
      setDescriptionChanged(false);
      
      // Focus back to description field
      if (descriptionInputRef.current) {
        descriptionInputRef.current.focus();
      }
      
      showNotification('Edit cancelled', 'warning');
    }
  };

  const handleEditRecord = (record: FinancialRecord) => {
    setEditingRecord(record);
    
    // Store original exchange rate if not already editing
    if (!editingExchangeRate && currentRate) {
      setOriginalExchangeRate(currentRate);
    }
    
    // Load record data into form with proper formatting
    setFormData({
      recordType: record.record_type,
      accountType: record.account_type,
      date: record.record_date.split('T')[0],
      description: record.description,
      amountVnd: Math.abs(record.amount_vnd).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
      amountUsd: Math.abs(record.amount_usd).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      taxPercent: record.tax_percent.toString(),
      taxVnd: record.tax_amount_vnd === 0 ? '0' : Math.abs(record.tax_amount_vnd).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
      taxUsd: record.tax_amount_usd === 0 ? '0.0' : Math.abs(record.tax_amount_usd).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      categoryId: record.category_id ? record.category_id.toString() : ''
    });
    
    // Set exchange rate to the record's exchange rate
    if (record.ExchangeRate) {
      setEditingExchangeRate(record.ExchangeRate);
      setCurrentRate(record.ExchangeRate);
      setNewExchangeRate(formatNumber(record.ExchangeRate.vnd_to_usd));
    }

    // Focus on VND amount field instead of description
    setTimeout(() => {
      const vndInput = document.querySelector('.amount-input-vnd') as HTMLInputElement;
      if (vndInput) {
        vndInput.focus();
        vndInput.select();
      }
    }, 100);
    setDescriptionChanged(false); // Reset on programmatic set
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRecord(null);
  };

  // Auto-queue balance recalculation after record operations
  const queueBalanceUpdate = async () => {
    try {
      // Queue balance recalculation with low priority to avoid overloading
      await cacheService.recalculateBalance(formData.accountType);
      
      // Fetch updated balance after a short delay to allow queue processing
      setTimeout(() => {
        fetchCurrentBalance();
      }, 2000);
    } catch (error) {
      console.error('Error queuing balance update:', error);
      // Don't show error notification as this is a background operation
    }
  };

  const validateFormData = () => {
    // Check for formula errors first
    const hasFormulaErrors = Object.values(formulaErrors).some(error => error);
    if (hasFormulaErrors) {
      alert('Please fix the formula errors before saving.');
      return false;
    }

    const vndAmount = parseFloat(formData.amountVnd.replace(/,/g, '')) || 0;
    const usdAmount = parseFloat(formData.amountUsd.replace(/,/g, '')) || 0;
    const taxPercent = parseFloat(formData.taxPercent) || 0;
    const taxVnd = parseFloat(formData.taxVnd.replace(/,/g, '')) || 0;
    const taxUsd = parseFloat(formData.taxUsd.replace(/,/g, '')) || 0;

    // Use editing exchange rate if editing, otherwise use current rate
    const rateToUse = editingExchangeRate || currentRate;
    
    if (!rateToUse) {
      alert('Exchange rate is not available. Please set an exchange rate first.');
      // Focus on exchange rate input
      const exchangeRateInput = document.querySelector('.rate-input') as HTMLInputElement;
      if (exchangeRateInput) exchangeRateInput.focus();
      return false;
    }

    if (!formData.description.trim()) {
      alert('Description is required.');
      // Focus on description input
      const descriptionInput = document.querySelector('input[title="Record description (auto-parsing enabled)"]') as HTMLInputElement;
      if (descriptionInput) descriptionInput.focus();
      return false;
    }

    if (!formData.categoryId) {
      alert('Please select a category.');
      // Focus on category select
      const categorySelect = document.querySelector('select[title="Select category"]') as HTMLSelectElement;
      if (categorySelect) categorySelect.focus();
      return false;
    }

    // Allow zero amounts, but all amounts must be zero together
    const isVndZero = vndAmount === 0;
    const isUsdZero = usdAmount === 0;
    const isTaxVndZero = taxVnd === 0;
    const isTaxUsdZero = taxUsd === 0;
    const isTaxPercentZero = taxPercent === 0;

    // If any amount is zero, all must be zero
    if (isVndZero || isUsdZero) {
      if (!isVndZero || !isUsdZero || !isTaxVndZero || !isTaxUsdZero) {
        alert('If VND or USD amount is zero, all amounts (VND, USD, and tax amounts) must be zero.');
        return false;
      }
    }

    // Validate currency conversion accuracy
    if (vndAmount > 0 && usdAmount > 0) {
      const expectedUsd = parseFloat((vndAmount / rateToUse.vnd_to_usd).toFixed(1));
      const expectedVnd = Math.round(usdAmount * rateToUse.vnd_to_usd);
      
      if (Math.abs(expectedUsd - usdAmount) > 0.1) {
        alert(`Currency conversion mismatch. Expected USD: ${expectedUsd.toFixed(1)}, but got: ${usdAmount.toFixed(1)}`);
        const usdInput = document.querySelector('.amount-input-usd') as HTMLInputElement;
        if (usdInput) usdInput.focus();
        return false;
      }
    }

    // Validate tax calculations
    if (taxPercent > 0) {
      if (vndAmount > 0) {
        let expectedTaxVnd: number;
        
        // Different calculation for income vs expense
        if (formData.recordType === 'income') {
          // For income: entered amount is gross (includes tax)
          expectedTaxVnd = Math.round(vndAmount - (vndAmount / (1 + taxPercent / 100)));
        } else {
          // For expense: entered amount is net (before tax)
          expectedTaxVnd = Math.round(vndAmount * (taxPercent / 100));
        }
        
        if (Math.abs(expectedTaxVnd - taxVnd) > 1) {
          alert(`Tax calculation mismatch for VND. Expected: ${expectedTaxVnd}, but got: ${taxVnd}`);
          const taxVndInput = document.querySelector('.tax-input-vnd') as HTMLInputElement;
          if (taxVndInput) taxVndInput.focus();
          return false;
        }
      }
      
      if (usdAmount > 0) {
        let expectedTaxUsd: number;
        
        // Different calculation for income vs expense
        if (formData.recordType === 'income') {
          // For income: entered amount is gross (includes tax)
          expectedTaxUsd = parseFloat((usdAmount - (usdAmount / (1 + taxPercent / 100))).toFixed(1));
        } else {
          // For expense: entered amount is net (before tax)
          expectedTaxUsd = parseFloat((usdAmount * (taxPercent / 100)).toFixed(1));
        }
        
        if (Math.abs(expectedTaxUsd - taxUsd) > 0.1) {
          alert(`Tax calculation mismatch for USD. Expected: ${expectedTaxUsd.toFixed(1)}, but got: ${taxUsd.toFixed(1)}`);
          const taxUsdInput = document.querySelector('.tax-input-usd') as HTMLInputElement;
          if (taxUsdInput) taxUsdInput.focus();
          return false;
        }
      }
    }

    return true;
  };

  const handleSaveRecord = async (recordData: any) => {
    try {
      if (editingRecord) {
        await recordService.update(editingRecord.id, recordData);
        showNotification('Record updated successfully', 'success');
      } else {
        await recordService.create(recordData);
        showNotification('Record created successfully', 'success');
      }
      
      // Queue balance recalculation automatically
      queueBalanceUpdate();
      
      fetchRecords();
      setShowModal(false);
      setEditingRecord(null);
    } catch (error) {
      console.error('Error saving record:', error);
      showNotification('Error saving record', 'error');
    }
  };

  const handleFormSave = async () => {
    if (!validateFormData()) {
      return;
    }

    try {
      const vndAmount = parseFloat(formData.amountVnd.replace(/,/g, '')) || 0;
      const usdAmount = parseFloat(formData.amountUsd.replace(/,/g, '')) || 0;
      
      const taxPercent = parseFloat(formData.taxPercent) || 0;
      const taxVnd = taxPercent === 0 ? 0 : (parseFloat(formData.taxVnd.replace(/,/g, '')) || 0);
      const taxUsd = taxPercent === 0 ? 0 : (parseFloat(formData.taxUsd.replace(/,/g, '')) || 0);

      const recordData = {
        record_type: formData.recordType,
        account_type: formData.accountType,
        record_date: formData.date,
        description: formData.description,
        amount_vnd: formData.recordType === 'expense' ? -Math.abs(vndAmount) : Math.abs(vndAmount),
        amount_usd: formData.recordType === 'expense' ? -Math.abs(usdAmount) : Math.abs(usdAmount),
        tax_percent: taxPercent,
        tax_amount_vnd: taxVnd === 0 ? 0 : -Math.abs(taxVnd), // Always negative
        tax_amount_usd: taxUsd === 0 ? 0 : -Math.abs(taxUsd), // Always negative
        category_id: formData.categoryId ? parseInt(formData.categoryId) : null
      };

      // Check if editing or creating
      if (editingRecord) {
        // Update existing record
        await recordService.update(editingRecord.id, recordData);
        showNotification('Record updated successfully', 'success');
        
        // Clear editing state
        setEditingRecord(null);
      } else {
        // Create new record
        await recordService.create(recordData);
        showNotification('Record created successfully', 'success');
      }
      
      // Queue balance recalculation automatically
      queueBalanceUpdate();
      
      // Restore original exchange rate if editing was in progress
      if (editingExchangeRate && originalExchangeRate) {
        setCurrentRate(originalExchangeRate);
        setNewExchangeRate(formatNumber(originalExchangeRate.vnd_to_usd));
        setOriginalExchangeRate(null);
        setEditingExchangeRate(null);
      }
      
      // Reset form but keep category selection for new records only
      const currentCategoryId = editingRecord ? '' : formData.categoryId;
      setFormData({
        recordType: 'expense',
        accountType: 'company',
        date: new Date().toISOString().split('T')[0],
        description: '',
        amountVnd: '0',
        amountUsd: '0.0',
        taxPercent: '0',
        taxVnd: '0',
        taxUsd: '0.0',
        categoryId: currentCategoryId
      });
      setLastModifiedField('');
      setPreviousFormValues({
        amountVnd: '0',
        amountUsd: '0.0',
        taxPercent: '0',
        taxVnd: '0',
        taxUsd: '0.0'
      });
      
      // Clear LLM state after saving
      clearLLMState();
      
      fetchRecords();
      
      // Focus back to description field for quick entry
      if (descriptionInputRef.current) {
        descriptionInputRef.current.focus();
      }
    } catch (error) {
      console.error('Error saving record:', error);
      showNotification('Error saving record', 'error');
    }
  };

  const handleDeleteRecord = async (record: FinancialRecord) => {
    const confirmMessage = `Are you sure you want to delete this record?\n\nDescription: ${record.description}\nDate: ${format(new Date(record.record_date), 'MM/dd/yyyy')}\nAmount: ${formatCurrency(record.amount_vnd, 'vnd')}`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      await recordService.delete(record.id);
      
      // Refresh records to reflect the deletion
      await fetchRecords();
      
      // Queue balance recalculation automatically after deletion
      queueBalanceUpdate();
      
      showNotification('Record deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting record:', error);
      showNotification('Error deleting record', 'error');
    }
  };

  // Bulk delete handlers
  const handleToggleSelectRecord = (recordId: number) => {
    setSelectedRecordIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recordId)) {
        newSet.delete(recordId);
      } else {
        newSet.add(recordId);
      }
      return newSet;
    });
  };

  const handleToggleSelectAll = () => {
    if (selectedRecordIds.size === records.length && records.length > 0) {
      // Deselect all
      setSelectedRecordIds(new Set());
    } else {
      // Select all
      setSelectedRecordIds(new Set(records.map(r => r.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRecordIds.size === 0) {
      showNotification('Please select records to delete', 'warning');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedRecordIds.size} selected record${selectedRecordIds.size > 1 ? 's' : ''}?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setBulkDeleteLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      const recordIdsArray = Array.from(selectedRecordIds);
      for (const recordId of recordIdsArray) {
        try {
          await recordService.delete(recordId);
          successCount++;
        } catch (error) {
          console.error(`Error deleting record ${recordId}:`, error);
          errorCount++;
        }
      }

      // Clear selection
      setSelectedRecordIds(new Set());

      // Refresh records
      await fetchRecords();

      // Queue balance recalculation
      queueBalanceUpdate();

      if (errorCount === 0) {
        showNotification(`Successfully deleted ${successCount} record${successCount > 1 ? 's' : ''}`, 'success');
      } else {
        showNotification(`Deleted ${successCount} record${successCount > 1 ? 's' : ''}, ${errorCount} failed`, 'warning');
      }
    } catch (error) {
      console.error('Error in bulk delete:', error);
      showNotification('Error deleting records', 'error');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleBulkSetZeroAmount = async () => {
    if (selectedRecordIds.size === 0) {
      showNotification('Please select records to set zero amounts', 'warning');
      return;
    }

    const confirmMessage = `Are you sure you want to set zero amounts for ${selectedRecordIds.size} selected record${selectedRecordIds.size > 1 ? 's' : ''}?\n\nThis will set Amount VND, Amount USD, Tax VND, Tax USD, and Tax Percent to zero.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setBulkDeleteLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      const recordIdsArray = Array.from(selectedRecordIds);
      
      for (const recordId of recordIdsArray) {
        try {
          // Find the record to get its current data
          const record = records.find(r => r.id === recordId);
          if (!record) {
            errorCount++;
            continue;
          }

          // Update the record with zero amounts
          await recordService.update(recordId, {
            ...record,
            amount_vnd: 0,
            amount_usd: 0,
            tax_amount_vnd: 0,
            tax_amount_usd: 0,
            tax_percent: 0
          });
          successCount++;
        } catch (error) {
          console.error(`Error setting zero amount for record ${recordId}:`, error);
          errorCount++;
        }
      }

      // Clear selection
      setSelectedRecordIds(new Set());

      // Refresh records
      await fetchRecords();

      // Queue balance recalculation
      queueBalanceUpdate();

      if (errorCount === 0) {
        showNotification(`Successfully set zero amounts for ${successCount} record${successCount > 1 ? 's' : ''}`, 'success');
      } else {
        showNotification(`Updated ${successCount} record${successCount > 1 ? 's' : ''}, ${errorCount} failed`, 'warning');
      }
    } catch (error) {
      console.error('Error in bulk set zero amount:', error);
      showNotification('Error setting zero amounts', 'error');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // This function is kept for future export functionality
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleExportRecords = async () => {
    try {
      const params: any = {};
      
      if (filters.startDate) params.startDate = format(filters.startDate, 'yyyy-MM-dd');
      if (filters.endDate) params.endDate = format(filters.endDate, 'yyyy-MM-dd');
      if (filters.recordType) params.recordType = filters.recordType;
      if (filters.accountType) params.accountType = filters.accountType;
      if (filters.categoryId) params.categoryId = filters.categoryId;
      if (filters.search) params.search = filters.search;

      const response = await recordService.export(params);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'records_export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting records:', error);
    }
  };

  const formatCurrency = (amount: number | string, currency: Currency) => {
    // Safely convert to number and handle NaN
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    const safeAmount = isNaN(numAmount) ? 0 : numAmount;
    
    if (currency === 'vnd') {
      // Format VND with commas and no decimals, consistent with form inputs
      const formatted = Math.abs(safeAmount).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      return `${safeAmount < 0 ? '-' : ''}${formatted} ₫`;
    } else {
      // Format USD with commas and 1 decimal place, consistent with form inputs
      const formatted = Math.abs(safeAmount).toLocaleString('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      });
      return `${safeAmount < 0 ? '-' : ''}$${formatted}`;
    }
  };

  const formatNumber = (number: number) => {
    // Handle invalid numbers
    if (isNaN(number) || number === null || number === undefined) {
      return '0';
    }
    return Math.round(number).toLocaleString('en-US');
  };

  // This function is kept for future numeric input formatting needs
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatNumberInput = (e: React.FormEvent<HTMLInputElement>, maxLength: number) => {
    const input = e.currentTarget;
    let value = input.value.replace(/[^0-9]/g, '');
    if (value.length > maxLength) {
      value = value.slice(0, maxLength);
    }
    // Add comma formatting
    const formatted = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    input.value = formatted;
  };

  const formatExchangeRateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length > 6) {
      value = value.slice(0, 6);
    }
    const formatted = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    setNewExchangeRate(formatted);
  };

  const handleAmountVndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Clear any existing formula error for this field
    setFormulaErrors(prev => ({ ...prev, amountVnd: false }));
    
    if (isFormula(value)) {
      // Don't format formulas, just store as-is
      setFormData(prev => ({ ...prev, amountVnd: value }));
    } else {
      // Allow empty value during editing
      if (value === '') {
        setFormData(prev => ({ ...prev, amountVnd: '' }));
      } else {
        // Format as usual for regular numbers
        const numericValue = value.replace(/[^0-9]/g, '');
        const formatted = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        setFormData(prev => ({ ...prev, amountVnd: formatted }));
      }
    }
    setLastModifiedField('amountVnd');
  };

  const handleAmountUsdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Clear any existing formula error for this field
    setFormulaErrors(prev => ({ ...prev, amountUsd: false }));
    
    if (isFormula(value)) {
      // Don't format formulas, just store as-is
      setFormData(prev => ({ ...prev, amountUsd: value }));
      setLastModifiedField('amountUsd');
    } else {
      const cleanValue = value.replace(/,/g, '');
      // Allow numbers with up to 1 decimal place or empty
      if (cleanValue === '' || /^\d*\.?\d?$/.test(cleanValue)) {
        // Store the value as-is without formatting during input
        setFormData(prev => ({ ...prev, amountUsd: value }));
        setLastModifiedField('amountUsd');
      }
    }
  };



  const handleTaxVndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Clear any existing formula error for this field
    setFormulaErrors(prev => ({ ...prev, taxVnd: false }));
    
    if (isFormula(value)) {
      // Don't format formulas, just store as-is
      setFormData(prev => ({ ...prev, taxVnd: value }));
    } else {
      // Allow empty value during editing
      if (value === '') {
        setFormData(prev => ({ ...prev, taxVnd: '' }));
      } else {
        // Format as usual for regular numbers
        const numericValue = value.replace(/[^0-9]/g, '');
        const formatted = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        setFormData(prev => ({ ...prev, taxVnd: formatted }));
      }
    }
    setLastModifiedField('taxVnd');
  };

  const handleTaxUsdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Clear any existing formula error for this field
    setFormulaErrors(prev => ({ ...prev, taxUsd: false }));
    
    if (isFormula(value)) {
      // Don't format formulas, just store as-is
      setFormData(prev => ({ ...prev, taxUsd: value }));
      setLastModifiedField('taxUsd');
    } else {
      const cleanValue = value.replace(/,/g, '');
      // Allow numbers with up to 1 decimal place or empty
      if (cleanValue === '' || /^\d*\.?\d?$/.test(cleanValue)) {
        // Store the value as-is without formatting during input
        setFormData(prev => ({ ...prev, taxUsd: value }));
        setLastModifiedField('taxUsd');
      }
    }
  };

  // Handle focus events to store previous values and set last modified field
  const handleFocus = (fieldName: string, e: React.FocusEvent<HTMLInputElement>) => {
    setLastModifiedField(fieldName);
    setPreviousFormValues({
      amountVnd: formData.amountVnd,
      amountUsd: formData.amountUsd,
      taxPercent: formData.taxPercent,
      taxVnd: formData.taxVnd,
      taxUsd: formData.taxUsd
    });
    
    // Select all text on first focus for easy replacement
    if (!fieldFocusState[fieldName]) {
      setTimeout(() => {
        e.target.select();
      }, 0);
      setFieldFocusState(prev => ({ ...prev, [fieldName]: true }));
    }
  };

  // Handle double click to force recalculation - use the double-clicked field as base
  const handleDoubleClick = (fieldName: string) => {
    const rateToUse = editingExchangeRate || currentRate;
    if (!rateToUse) return;
    
    const vndAmount = parseFloat(formData.amountVnd.replace(/,/g, '')) || 0;
    const usdAmount = parseFloat(formData.amountUsd.replace(/,/g, '')) || 0;
    const taxPercent = parseFloat(formData.taxPercent) || 0;
    
    let updates: any = {};
    
    if (fieldName === 'amountVnd' && vndAmount > 0) {
      // VND is the base - calculate USD from VND
      const calculatedUsd = parseFloat((vndAmount / rateToUse.vnd_to_usd).toFixed(1));
      updates.amountUsd = calculatedUsd.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      
      // Recalculate tax amounts based on VND
      if (taxPercent > 0) {
        const { taxVnd, taxUsd } = calculateTaxAmounts(vndAmount, calculatedUsd, taxPercent, rateToUse, formData.recordType);
        updates.taxVnd = taxVnd;
        updates.taxUsd = taxUsd;
      }
    } else if (fieldName === 'amountUsd' && usdAmount > 0) {
      // USD is the base - calculate VND from USD  
      const calculatedVnd = Math.round(usdAmount * rateToUse.vnd_to_usd);
      updates.amountVnd = calculatedVnd.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      
      // Recalculate tax amounts based on USD
      if (taxPercent > 0) {
        const { taxVnd, taxUsd } = calculateTaxAmounts(calculatedVnd, usdAmount, taxPercent, rateToUse, formData.recordType);
        updates.taxVnd = taxVnd;
        updates.taxUsd = taxUsd;
      }
    } else if (fieldName === 'taxPercent' && taxPercent > 0) {
      // Tax percent is the base - recalculate tax amounts
      const { taxVnd, taxUsd } = calculateTaxAmounts(vndAmount, usdAmount, taxPercent, rateToUse, formData.recordType);
      updates.taxVnd = taxVnd;
      updates.taxUsd = taxUsd;
    } else if (fieldName === 'taxVnd' && vndAmount > 0) {
      // Tax VND is the base - calculate tax percent and tax USD
      const taxVndAmount = parseFloat(formData.taxVnd.replace(/,/g, '')) || 0;
      if (taxVndAmount > 0) {
        const newTaxPercent = ((taxVndAmount / vndAmount) * 100);
        const newTaxUsd = parseFloat((taxVndAmount / rateToUse.vnd_to_usd).toFixed(1));
        updates.taxPercent = newTaxPercent.toFixed(1);
        updates.taxUsd = newTaxUsd.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      }
    } else if (fieldName === 'taxUsd' && usdAmount > 0) {
      // Tax USD is the base - calculate tax percent and tax VND
      const taxUsdAmount = parseFloat(formData.taxUsd.replace(/,/g, '')) || 0;
      if (taxUsdAmount > 0) {
        const newTaxPercent = ((taxUsdAmount / usdAmount) * 100);
        const newTaxVnd = Math.round(taxUsdAmount * rateToUse.vnd_to_usd);
        updates.taxPercent = newTaxPercent.toFixed(1);
        updates.taxVnd = newTaxVnd.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }
    }
    
    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
  };

  // Helper function to trigger recalculation with specific field values
  const triggerRecalculation = (fieldName: string, fieldValue: string) => {
    const rateToUse = editingExchangeRate || currentRate;
    if (!rateToUse) return;
    
    // Get current values but override the specific field with the new value
    const currentVnd = fieldName === 'amountVnd' ? fieldValue : formData.amountVnd;
    const currentUsd = fieldName === 'amountUsd' ? fieldValue : formData.amountUsd;
    const currentTaxPercent = fieldName === 'taxPercent' ? fieldValue : formData.taxPercent;
    const currentTaxVnd = fieldName === 'taxVnd' ? fieldValue : formData.taxVnd;
    const currentTaxUsd = fieldName === 'taxUsd' ? fieldValue : formData.taxUsd;
    
    const vndAmount = parseFloat(currentVnd.replace(/,/g, '')) || 0;
    const usdAmount = parseFloat(currentUsd.replace(/,/g, '')) || 0;
    const taxPercent = parseFloat(currentTaxPercent) || 0;
    
    let updates: any = {};
    
    if (fieldName === 'amountVnd' && vndAmount > 0) {
      // VND is the base - calculate USD from VND
      const calculatedUsd = parseFloat((vndAmount / rateToUse.vnd_to_usd).toFixed(1));
      updates.amountUsd = calculatedUsd.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      
      // Recalculate tax amounts based on VND
      if (taxPercent > 0) {
        const { taxVnd, taxUsd } = calculateTaxAmounts(vndAmount, calculatedUsd, taxPercent, rateToUse, formData.recordType);
        updates.taxVnd = taxVnd;
        updates.taxUsd = taxUsd;
      }
    } else if (fieldName === 'amountUsd' && usdAmount > 0) {
      // USD is the base - calculate VND from USD  
      const calculatedVnd = Math.round(usdAmount * rateToUse.vnd_to_usd);
      updates.amountVnd = calculatedVnd.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      
      // Recalculate tax amounts based on USD
      if (taxPercent > 0) {
        const { taxVnd, taxUsd } = calculateTaxAmounts(calculatedVnd, usdAmount, taxPercent, rateToUse, formData.recordType);
        updates.taxVnd = taxVnd;
        updates.taxUsd = taxUsd;
      }
    } else if (fieldName === 'taxPercent' && taxPercent > 0) {
      // Tax percent is the base - recalculate tax amounts
      const { taxVnd, taxUsd } = calculateTaxAmounts(vndAmount, usdAmount, taxPercent, rateToUse, formData.recordType);
      updates.taxVnd = taxVnd;
      updates.taxUsd = taxUsd;
    } else if (fieldName === 'taxVnd' && vndAmount > 0) {
      // Tax VND is the base - calculate tax percent and tax USD
      const taxVndAmount = parseFloat(currentTaxVnd.replace(/,/g, '')) || 0;
      if (taxVndAmount > 0) {
        const newTaxPercent = ((taxVndAmount / vndAmount) * 100);
        const newTaxUsd = parseFloat((taxVndAmount / rateToUse.vnd_to_usd).toFixed(1));
        updates.taxPercent = newTaxPercent.toFixed(1);
        updates.taxUsd = newTaxUsd.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      }
    } else if (fieldName === 'taxUsd' && usdAmount > 0) {
      // Tax USD is the base - calculate tax percent and tax VND
      const taxUsdAmount = parseFloat(currentTaxUsd.replace(/,/g, '')) || 0;
      if (taxUsdAmount > 0) {
        const newTaxPercent = ((taxUsdAmount / usdAmount) * 100);
        const newTaxVnd = Math.round(taxUsdAmount * rateToUse.vnd_to_usd);
        updates.taxPercent = newTaxPercent.toFixed(1);
        updates.taxVnd = newTaxVnd.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }
    }
    
    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
      
      // Update previous form values for all calculated fields to prevent false "changes"
      const newPreviousValues = { ...previousFormValues };
      Object.keys(updates).forEach(key => {
        if (key === 'amountVnd') newPreviousValues.amountVnd = updates[key];
        if (key === 'amountUsd') newPreviousValues.amountUsd = updates[key];
        if (key === 'taxPercent') newPreviousValues.taxPercent = updates[key];
        if (key === 'taxVnd') newPreviousValues.taxVnd = updates[key];
        if (key === 'taxUsd') newPreviousValues.taxUsd = updates[key];
      });
      setPreviousFormValues(newPreviousValues);
    }
  };

  // Comprehensive recalculation function based on which field was last modified
  const recalculateAll = (triggerField?: string) => {
    const vndAmount = parseFloat(formData.amountVnd.replace(/,/g, '')) || 0;
    const usdAmount = parseFloat(formData.amountUsd.replace(/,/g, '')) || 0;
    const taxPercent = parseFloat(formData.taxPercent) || 0;
    const taxVnd = parseFloat(formData.taxVnd.replace(/,/g, '')) || 0;
    const taxUsd = parseFloat(formData.taxUsd.replace(/,/g, '')) || 0;
    
    // Use editing exchange rate if editing, otherwise use current rate
    const rateToUse = editingExchangeRate || currentRate;
    if (!rateToUse) return;
    
    let updates: any = {};
    const fieldToUse = triggerField || lastModifiedField;
    
    // Check if value actually changed - if not, skip recalculation
    const currentFieldValue = fieldToUse === 'amountVnd' ? formData.amountVnd :
                            fieldToUse === 'amountUsd' ? formData.amountUsd :
                            fieldToUse === 'taxPercent' ? formData.taxPercent :
                            fieldToUse === 'taxVnd' ? formData.taxVnd :
                            fieldToUse === 'taxUsd' ? formData.taxUsd : '';
    
    const previousFieldValue = fieldToUse === 'amountVnd' ? previousFormValues.amountVnd :
                             fieldToUse === 'amountUsd' ? previousFormValues.amountUsd :
                             fieldToUse === 'taxPercent' ? previousFormValues.taxPercent :
                             fieldToUse === 'taxVnd' ? previousFormValues.taxVnd :
                             fieldToUse === 'taxUsd' ? previousFormValues.taxUsd : '';
    
    // If no change, don't recalculate
    if (currentFieldValue === previousFieldValue) {
      return;
    }
    
    // Handle amount conversions - ONLY calculate the opposite field
    if (fieldToUse === 'amountUsd' && usdAmount > 0) {
      // USD was modified - calculate VND from USD
      const calculatedVnd = Math.round(usdAmount * rateToUse.vnd_to_usd);
      if (Math.abs(calculatedVnd - vndAmount) > 1) {
        updates.amountVnd = calculatedVnd.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }
    } else if (fieldToUse === 'amountVnd' && vndAmount > 0) {
      // VND was modified - calculate USD from VND
      const calculatedUsd = parseFloat((vndAmount / rateToUse.vnd_to_usd).toFixed(1));
      if (Math.abs(calculatedUsd - usdAmount) > 0.1) {
        updates.amountUsd = calculatedUsd.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      }
    }
    
    // Handle tax calculations
    if (fieldToUse === 'taxPercent') {
      // Tax percent was modified - calculate tax amounts from amounts
      const finalVnd = updates.amountVnd ? parseFloat(updates.amountVnd.replace(/,/g, '')) : vndAmount;
      const finalUsd = updates.amountUsd ? parseFloat(updates.amountUsd) : usdAmount;
      const { taxVnd: newTaxVnd, taxUsd: newTaxUsd } = calculateTaxAmounts(finalVnd, finalUsd, taxPercent, rateToUse || undefined, formData.recordType);
      updates.taxVnd = newTaxVnd;
      updates.taxUsd = newTaxUsd;
    } else if (fieldToUse === 'taxVnd' && taxVnd > 0 && vndAmount > 0) {
      // Tax VND was modified - calculate tax percent and tax USD
      const newTaxPercent = ((taxVnd / vndAmount) * 100);
      const newTaxUsd = parseFloat((taxVnd / rateToUse.vnd_to_usd).toFixed(1));
      updates.taxPercent = newTaxPercent.toFixed(1);
      updates.taxUsd = newTaxUsd.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    } else if (fieldToUse === 'taxUsd' && taxUsd > 0 && usdAmount > 0) {
      // Tax USD was modified - calculate tax percent and tax VND
      const newTaxPercent = ((taxUsd / usdAmount) * 100);
      const newTaxVnd = Math.round(taxUsd * rateToUse.vnd_to_usd);
      updates.taxPercent = newTaxPercent.toFixed(1);
      updates.taxVnd = newTaxVnd.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    } else if ((fieldToUse === 'amountVnd' || fieldToUse === 'amountUsd') && taxPercent > 0) {
      // Amount was modified and there's a tax percent - recalculate tax amounts
      const finalVnd = updates.amountVnd ? parseFloat(updates.amountVnd.replace(/,/g, '')) : vndAmount;
      const finalUsd = updates.amountUsd ? parseFloat(updates.amountUsd) : usdAmount;
      const { taxVnd: newTaxVnd, taxUsd: newTaxUsd } = calculateTaxAmounts(finalVnd, finalUsd, taxPercent, rateToUse || undefined, formData.recordType);
      updates.taxVnd = newTaxVnd;
      updates.taxUsd = newTaxUsd;
    }
    
    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
  };

  // Helper function to remove leading zeros
  const removeLeadingZeros = (value: string): string => {
    // Handle decimal numbers (USD amounts)
    if (value.includes('.')) {
      const [integerPart, decimalPart] = value.split('.');
      const cleanInteger = integerPart.replace(/^0+/, '') || '0';
      return `${cleanInteger}.${decimalPart}`;
    }
    // Handle integer numbers (VND amounts)
    return value.replace(/^0+/, '') || '0';
  };

  // Blur handlers for recalculation and formatting
  const handleAmountVndBlur = () => {
    const previousValue = previousFormValues.amountVnd;
    
    if (isFormula(formData.amountVnd)) {
      // Calculate formula result
      const { result, isValid } = calculateFormula(formData.amountVnd);
      
      if (isValid) {
        // Convert to number and format
        const numValue = parseFloat(result) || 0;
        const formattedValue = numValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        
        // Set the result and clear formula error
        setFormData(prev => ({ ...prev, amountVnd: formattedValue }));
        setFormulaErrors(prev => ({ ...prev, amountVnd: false }));
        setPreviousFormValues(prev => ({ ...prev, amountVnd: formattedValue }));
        
        // Set lastModifiedField to trigger auto-calculations
        setLastModifiedField('amountVnd');
        
        // Trigger automatic recalculation with the new value
        setTimeout(() => {
          triggerRecalculation('amountVnd', formattedValue);
        }, 0);
      } else {
        // Mark as error
        setFormulaErrors(prev => ({ ...prev, amountVnd: true }));
        return; // Don't recalculate if formula is invalid
      }
    } else {
      // Handle empty value - only set default if truly empty after user interaction
      if (formData.amountVnd === '' || formData.amountVnd === undefined) {
        // Allow staying empty during editing - only set default if field loses focus and is completely empty
        const defaultValue = '0';
        setFormData(prev => ({ ...prev, amountVnd: defaultValue }));
        setPreviousFormValues(prev => ({ ...prev, amountVnd: defaultValue }));
        
        // Only recalculate if value actually changed
        if (defaultValue !== previousValue) {
          recalculateAll('amountVnd');
        }
        return;
      }
      
      // Remove leading zeros for regular numbers
      const cleanValue = removeLeadingZeros(formData.amountVnd.replace(/,/g, ''));
      const numValue = parseFloat(cleanValue) || 0;
      const formattedValue = numValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      
      // If VND is set to zero, auto-set USD and tax amounts to zero
      if (numValue === 0) {
        setFormData(prev => ({ 
          ...prev, 
          amountVnd: formattedValue,
          amountUsd: '0.0',
          taxVnd: '0',
          taxUsd: '0.0',
          taxPercent: '0'
        }));
        setPreviousFormValues(prev => ({ 
          ...prev, 
          amountVnd: formattedValue,
          amountUsd: '0.0',
          taxVnd: '0',
          taxUsd: '0.0'
        }));
        return;
      }
      
      if (formattedValue !== formData.amountVnd) {
        setFormData(prev => ({ ...prev, amountVnd: formattedValue }));
      }
      
      // Only recalculate if value actually changed from what user originally had
      if (formattedValue !== previousValue) {
        setPreviousFormValues(prev => ({ ...prev, amountVnd: formattedValue }));
        recalculateAll('amountVnd');
      } else {
        // Just update previous value without recalculating
        setPreviousFormValues(prev => ({ ...prev, amountVnd: formattedValue }));
      }
    }
  };

  const handleAmountUsdBlur = () => {
    const previousValue = previousFormValues.amountUsd;
    
    if (isFormula(formData.amountUsd)) {
      // Calculate formula result
      const { result, isValid } = calculateFormula(formData.amountUsd);
      
      if (isValid) {
        // Convert to number and format
        const numValue = parseFloat(result) || 0;
        const formattedValue = numValue.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        
        // Set the result and clear formula error
        setFormData(prev => ({ ...prev, amountUsd: formattedValue }));
        setFormulaErrors(prev => ({ ...prev, amountUsd: false }));
        setPreviousFormValues(prev => ({ ...prev, amountUsd: formattedValue }));
        
        // Set lastModifiedField to trigger auto-calculations
        setLastModifiedField('amountUsd');
        
        // Trigger automatic recalculation with the new value
        setTimeout(() => {
          triggerRecalculation('amountUsd', formattedValue);
        }, 0);
      } else {
        // Mark as error
        setFormulaErrors(prev => ({ ...prev, amountUsd: true }));
        return; // Don't recalculate if formula is invalid
      }
    } else {
      // Handle empty value - only set default if truly empty after user interaction
      if (formData.amountUsd === '' || formData.amountUsd === undefined) {
        // Allow staying empty during editing - only set default if field loses focus and is completely empty
        const defaultValue = '0.0';
        setFormData(prev => ({ ...prev, amountUsd: defaultValue }));
        setPreviousFormValues(prev => ({ ...prev, amountUsd: defaultValue }));
        
        // Only recalculate if value actually changed
        if (defaultValue !== previousValue) {
          recalculateAll('amountUsd');
        }
        return;
      }
      
      // Format for display when bluring (only non-formula values)
      const cleanValue = formData.amountUsd.replace(/,/g, '');
      const numValue = parseFloat(cleanValue) || 0;
      const formattedValue = numValue.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      
      if (formattedValue !== formData.amountUsd) {
        setFormData(prev => ({ ...prev, amountUsd: formattedValue }));
      }
      
      // Only recalculate if value actually changed from what user originally had
      if (formattedValue !== previousValue) {
        setPreviousFormValues(prev => ({ ...prev, amountUsd: formattedValue }));
        recalculateAll('amountUsd');
      } else {
        // Just update previous value without recalculating
        setPreviousFormValues(prev => ({ ...prev, amountUsd: formattedValue }));
      }
    }
  };

  const handleTaxPercentBlur = () => {
    const previousValue = previousFormValues.taxPercent;
    
    // Remove leading zeros from tax percent
    const cleanValue = removeLeadingZeros(formData.taxPercent);
    const numValue = parseFloat(cleanValue) || 0;
    const formattedValue = numValue.toString();
    
    if (formattedValue !== formData.taxPercent) {
      setFormData(prev => ({ ...prev, taxPercent: formattedValue }));
    }
    
    // Only recalculate if value actually changed from what user originally had
    if (formattedValue !== previousValue) {
      setPreviousFormValues(prev => ({ ...prev, taxPercent: formattedValue }));
      recalculateAll('taxPercent');
    } else {
      // Just update previous value without recalculating
      setPreviousFormValues(prev => ({ ...prev, taxPercent: formattedValue }));
    }
  };

  const handleTaxVndBlur = () => {
    const previousValue = previousFormValues.taxVnd;
    
    if (isFormula(formData.taxVnd)) {
      // Calculate formula result
      const { result, isValid } = calculateFormula(formData.taxVnd);
      
      if (isValid) {
        // Convert to number and format
        const numValue = parseFloat(result) || 0;
        const formattedValue = numValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        
        // Set the result and clear formula error
        setFormData(prev => ({ ...prev, taxVnd: formattedValue }));
        setFormulaErrors(prev => ({ ...prev, taxVnd: false }));
        setPreviousFormValues(prev => ({ ...prev, taxVnd: formattedValue }));
        
        // Set lastModifiedField to trigger auto-calculations
        setLastModifiedField('taxVnd');
        
        // Trigger automatic recalculation with the new value
        setTimeout(() => {
          triggerRecalculation('taxVnd', formattedValue);
        }, 0);
      } else {
        // Mark as error
        setFormulaErrors(prev => ({ ...prev, taxVnd: true }));
        return; // Don't recalculate if formula is invalid
      }
    } else {
      // Handle empty value - only set default if truly empty after user interaction
      if (formData.taxVnd === '' || formData.taxVnd === undefined) {
        // Allow staying empty during editing - only set default if field loses focus and is completely empty
        const defaultValue = '0';
        setFormData(prev => ({ ...prev, taxVnd: defaultValue }));
        setPreviousFormValues(prev => ({ ...prev, taxVnd: defaultValue }));
        
        // Only recalculate if value actually changed
        if (defaultValue !== previousValue) {
          recalculateAll('taxVnd');
        }
        return;
      }
      
      // Remove leading zeros for regular numbers
      const cleanValue = removeLeadingZeros(formData.taxVnd.replace(/,/g, ''));
      const numValue = parseFloat(cleanValue) || 0;
      const formattedValue = numValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      
      if (formattedValue !== formData.taxVnd) {
        setFormData(prev => ({ ...prev, taxVnd: formattedValue }));
      }
      
      // Only recalculate if value actually changed from what user originally had
      if (formattedValue !== previousValue) {
        setPreviousFormValues(prev => ({ ...prev, taxVnd: formattedValue }));
        recalculateAll('taxVnd');
      } else {
        // Just update previous value without recalculating
        setPreviousFormValues(prev => ({ ...prev, taxVnd: formattedValue }));
      }
    }
  };

  const handleTaxUsdBlur = () => {
    const previousValue = previousFormValues.taxUsd;
    
    if (isFormula(formData.taxUsd)) {
      // Calculate formula result
      const { result, isValid } = calculateFormula(formData.taxUsd);
      
      if (isValid) {
        // Convert to number and format
        const numValue = parseFloat(result) || 0;
        const formattedValue = numValue.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        
        // Set the result and clear formula error
        setFormData(prev => ({ ...prev, taxUsd: formattedValue }));
        setFormulaErrors(prev => ({ ...prev, taxUsd: false }));
        setPreviousFormValues(prev => ({ ...prev, taxUsd: formattedValue }));
        
        // Set lastModifiedField to trigger auto-calculations
        setLastModifiedField('taxUsd');
        
        // Trigger automatic recalculation with the new value
        setTimeout(() => {
          triggerRecalculation('taxUsd', formattedValue);
        }, 0);
      } else {
        // Mark as error
        setFormulaErrors(prev => ({ ...prev, taxUsd: true }));
        return; // Don't recalculate if formula is invalid
      }
    } else {
      // Handle empty value - only set default if truly empty after user interaction
      if (formData.taxUsd === '' || formData.taxUsd === undefined) {
        // Allow staying empty during editing - only set default if field loses focus and is completely empty
        const defaultValue = '0.0';
        setFormData(prev => ({ ...prev, taxUsd: defaultValue }));
        setPreviousFormValues(prev => ({ ...prev, taxUsd: defaultValue }));
        
        // Only recalculate if value actually changed
        if (defaultValue !== previousValue) {
          recalculateAll('taxUsd');
        }
        return;
      }
      
      // Format for display when bluring (only non-formula values)
      const cleanValue = formData.taxUsd.replace(/,/g, '');
      const numValue = parseFloat(cleanValue) || 0;
      const formattedValue = numValue.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      
      if (formattedValue !== formData.taxUsd) {
        setFormData(prev => ({ ...prev, taxUsd: formattedValue }));
      }
      
      // Only recalculate if value actually changed from what user originally had
      if (formattedValue !== previousValue) {
        setPreviousFormValues(prev => ({ ...prev, taxUsd: formattedValue }));
        recalculateAll('taxUsd');
      } else {
        // Just update previous value without recalculating
        setPreviousFormValues(prev => ({ ...prev, taxUsd: formattedValue }));
      }
    }
  };

  const formatTaxCurrency = (amount: number | string, currency: Currency) => {
    // Safely convert to number and handle NaN
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    const safeAmount = isNaN(numAmount) ? 0 : numAmount;
    
    // Return blank for zero tax amounts
    if (safeAmount === 0) {
      return '';
    }
    
    if (currency === 'vnd') {
      // Format VND with commas and no decimals, consistent with form inputs
      const formatted = Math.abs(safeAmount).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      return `${safeAmount < 0 ? '-' : ''}${formatted} ₫`;
    } else {
      // Format USD with commas and 1 decimal place, consistent with form inputs
      const formatted = Math.abs(safeAmount).toLocaleString('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      });
      return `${safeAmount < 0 ? '-' : ''}$${formatted}`;
    }
  };

  // Format report amount directly (AMOUNT + TAX)
  const ReportAmountCell = ({ record }: { record: FinancialRecord }) => {
    // Make sure we have valid numbers
    const safeAmountVnd = Number(record.amount_vnd) || 0;
    const safeAmountUsd = Number(record.amount_usd) || 0;
    const safeTaxVnd = Number(record.tax_amount_vnd) || 0;
    const safeTaxUsd = Number(record.tax_amount_usd) || 0;
    
    // Calculate total by adding the amount and tax (ensure numeric calculation)
    const totalVnd = safeAmountVnd + safeTaxVnd;
    const totalUsd = safeAmountUsd + safeTaxUsd;
    const isZero = totalVnd === 0 && totalUsd === 0;
    
    // Format the values for display
    const vndFormatted = formatCurrency(totalVnd, 'vnd');
    const usdFormatted = formatCurrency(totalUsd, 'usd');
    
    
    return (
      <div className="total-amount-cell" key={`report-${record.id}-${Date.now()}`}>
        <div className="total-vnd" data-record-id={record.record_id} data-type="report-vnd" style={{ color: isZero ? '#dc2626' : undefined }}>{vndFormatted}</div>
        <div className="total-usd" data-record-id={record.record_id} data-type="report-usd" style={{ color: isZero ? '#dc2626' : undefined }}>{usdFormatted}</div>
      </div>
    );
  };

  // Format total amount correctly (AMOUNT + TAX)
  const formatTotalAmount = (amountVnd: number, amountUsd: number, taxVnd: number, taxUsd: number) => {
    // Make sure we have valid numbers
    const safeAmountVnd = amountVnd || 0;
    const safeAmountUsd = amountUsd || 0;
    const safeTaxVnd = taxVnd || 0;
    const safeTaxUsd = taxUsd || 0;
    
    // Calculate total by adding the amount and tax
    const totalVnd = safeAmountVnd + safeTaxVnd;
    const totalUsd = safeAmountUsd + safeTaxUsd;
    
    // Format the values for display
    const vndFormatted = formatCurrency(totalVnd, 'vnd');
    const usdFormatted = formatCurrency(totalUsd, 'usd');
    
    return (
      <div className="total-amount-cell">
        <div className="total-vnd">{vndFormatted}</div>
        <div className="total-usd">{usdFormatted}</div>
      </div>
    );
  };

  const formatAmountCell = (amountVnd: number, amountUsd: number) => {
    const vndFormatted = formatCurrency(amountVnd, 'vnd');
    const usdFormatted = formatCurrency(amountUsd, 'usd');
    const isZero = amountVnd === 0 && amountUsd === 0;
    
    return (
      <div className="amount-cell">
        <div className="amount-vnd" style={{ color: isZero ? '#dc2626' : undefined }}>{vndFormatted}</div>
        <div className="amount-usd" style={{ color: isZero ? '#dc2626' : undefined }}>{usdFormatted}</div>
      </div>
    );
  };

  const formatTaxAmountCell = (taxVnd: number, taxUsd: number) => {
    // Return empty if no tax
    if (taxVnd === 0 && taxUsd === 0) {
      return <div className="tax-amount-cell"></div>;
    }
    
    const vndFormatted = formatTaxCurrency(taxVnd, 'vnd');
    const usdFormatted = formatTaxCurrency(taxUsd, 'usd');
    
    return (
      <div className="tax-amount-cell">
        <div className="tax-vnd">{vndFormatted}</div>
        <div className="tax-usd">{usdFormatted}</div>
      </div>
    );
  };

  // Handle duplicate record
  const handleDuplicateRecord = (record: FinancialRecord) => {
    // Clear any existing editing state
    setEditingRecord(null);
    
    // Use the current exchange rate
    const rateToUse = currentRate;
    if (!rateToUse) {
      showNotification('Exchange rate is required to duplicate a record', 'error');
      return;
    }
    
    // Calculate USD amount based on VND amount and current exchange rate
    const vndAmount = Math.abs(record.amount_vnd);
    const usdAmount = parseFloat((vndAmount / rateToUse.vnd_to_usd).toFixed(1));
    
    // Calculate tax amounts if applicable
    let taxVnd = '0';
    let taxUsd = '0.0';
    
    if (record.tax_percent > 0) {
      const { taxVnd: calculatedTaxVnd, taxUsd: calculatedTaxUsd } = 
        calculateTaxAmounts(vndAmount, usdAmount, record.tax_percent, rateToUse, record.record_type);
      taxVnd = calculatedTaxVnd;
      taxUsd = calculatedTaxUsd;
    }
    
    // Load record data into form with proper formatting and recalculated values
    setFormData({
      recordType: record.record_type,
      accountType: record.account_type,
      date: new Date().toISOString().split('T')[0], // Set to today's date for the new record
      description: record.description,
      amountVnd: vndAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
      amountUsd: usdAmount.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      taxPercent: record.tax_percent.toString(),
      taxVnd: taxVnd,
      taxUsd: taxUsd,
      categoryId: record.category_id ? record.category_id.toString() : ''
    });
    
    // Clear LLM state when duplicating a record
    clearLLMState();
    
    // Focus on VND amount field instead of description
    setTimeout(() => {
      const vndInput = document.querySelector('.amount-input-vnd') as HTMLInputElement;
      if (vndInput) {
        vndInput.focus();
        vndInput.select();
      }
    }, 100);
    
    showNotification('Record duplicated. Edit as needed and click Save to create a new record.', 'success');
    setDescriptionChanged(false); // Reset on programmatic set
  };



  // Only set descriptionChanged on user input
  const handleDescriptionInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev: FormDataType) => ({ ...prev, description: e.target.value }));
    setDescriptionChanged(true);
  };

  return (
    <div className="records-page">
      {/* Professional Header Panel */}
      <div className="records-header-panel">
        {/* Current Balance Section */}
        <div className="header-section balance-section">
          <div className="section-header">
            <div>
              <FontAwesomeIcon icon={faWallet} />
              <span>Current Balance</span>
            </div>
            {!balanceLoading && (
              <div className="balance-status">
                {balanceFromCache && (
                  <OverlayTrigger
                    placement="top"
                    overlay={<BootstrapTooltip>{balanceFresh ? 'Cache is fresh' : 'Cache may be outdated'}</BootstrapTooltip>}
                  >
                    <span className={`cache-indicator ${balanceFresh ? 'fresh' : 'stale'}`}>
                      <FontAwesomeIcon icon={balanceFresh ? faCheckCircle : faClock} />
                    </span>
                  </OverlayTrigger>
                )}
                <OverlayTrigger
                  placement="top"
                  overlay={<BootstrapTooltip>Refresh balance</BootstrapTooltip>}
                >
                  <Button 
                    variant="link"
                    className="refresh-balance-btn" 
                    onClick={forceRefreshBalance}
                    disabled={balanceLoading}
                    size="sm"
                  >
                    <FontAwesomeIcon icon={faSync} />
                  </Button>
                </OverlayTrigger>
              </div>
            )}
          </div>
          <div className="balance-display no-gap">
            {balanceLoading ? (
              <div className="balance-loading">
                <div className="balance-primary">Loading...</div>
                <div className="balance-secondary">Please wait...</div>
              </div>
            ) : (
              <>
                <div className="balance-primary">{formatCurrency(currentBalance.total_vnd || 0, 'vnd')}</div>
                <div className="balance-secondary">{formatCurrency(currentBalance.total_usd || 0, 'usd')}</div>
              </>
            )}
          </div>
        </div>

        {/* Account Type Section - consistent height and border */}
        <div className="header-section controls-section">
          <div className="control-label">
            <FontAwesomeIcon icon={faBuilding} />
            <span>Account</span>
          </div>
          <div className="radio-group">
            <label className="radio-option">
              <input 
                type="radio" 
                name="accountType" 
                value="company"
                checked={formData.accountType === 'company'}
                onChange={(e) => setFormData({...formData, accountType: e.target.value as AccountType})}
                style={{ margin: 0, width: 14, height: 14 }}
              />
              <span>Company</span>
            </label>
            <label className="radio-option" style={{ fontSize: 12, gap: 2 }}>
              <input 
                type="radio" 
                name="accountType" 
                value="personal" 
                checked={formData.accountType === 'personal'}
                onChange={(e) => setFormData({...formData, accountType: e.target.value as AccountType})}
                style={{ margin: 0, width: 14, height: 14 }}
              />
              <span>Personal</span>
            </label>
          </div>
        </div>

        {/* Exchange Rate Section - consistent height and border */}
        <div className="header-section exchange-section" style={{ flex: 1, maxWidth: 250, maxHeight: 250, display: 'flex', flexDirection: 'column', justifyContent: 'center', border: 'none', borderRadius: 8, padding: '8px 18px', boxSizing: 'border-box', backgroundColor: 'white' }}>
          <div className="section-header" style={{ marginBottom: 4, fontSize: 11 }}>
            <FontAwesomeIcon icon={faExchangeAlt} />
            <span>Exchange Rate</span>
            <span className="rate-label">VND/USD</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input 
              type="text" 
              value={newExchangeRate}
              onChange={formatExchangeRateInput}
              className="rate-input"
              title="Enter VND to USD exchange rate"
              maxLength={6}
              style={{ width: '80px', minWidth: '80px', textAlign: 'right', marginRight: 4, height: '36px', fontSize: '13px', padding: '0 8px', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', color: '#374151' }}
            />
            <Button 
              variant="success"
              className="rate-save-btn" 
              onClick={handleSaveExchangeRate}
              title="Save exchange rate"
              size="sm"
              style={{ minWidth: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
            >
              <FontAwesomeIcon icon={faSave} />
            </Button>
          </div>
        </div>

        {/* Quick Actions Section - consistent height, border, and wider */}
        <div className="header-section actions-section" style={{ minWidth: 220, minHeight: 64, display: 'flex', flexDirection: 'column', justifyContent: 'center', border: 'none', borderRadius: 8, background: '#fff', padding: '8px 18px', boxSizing: 'border-box', alignItems: 'center' }}>
          <div className="actions-controls" style={{ width: '100%' }}>
            <Dropdown>
              <Dropdown.Toggle 
                variant="primary" 
                id="quick-actions-dropdown"
                className="quick-actions-btn"
                disabled={copyLoading}
                title="Quick Actions"
                style={{ minWidth: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 32, fontSize: 14, padding: '0 16px' }}
              >
                <FontAwesomeIcon icon={faBolt} style={{ marginRight: 6 }} />
                Quick Actions
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item 
                  onClick={handleImportCustomerPayments}
                  disabled={importLoading}
                  title="Import customer payment records from Excel data"
                >
                  <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" />
                  Import Income Payments
                </Dropdown.Item>
                <Dropdown.Item 
                  onClick={handleImportExpensePayments}
                  disabled={expenseImportLoading}
                  title="Import expense payment records from Excel data"
                >
                  <FontAwesomeIcon icon={faCoins} className="me-2" />
                  Import Expense Payments
                </Dropdown.Item>
                <Dropdown.Item 
                  onClick={handleCopyFromPreviousMonth}
                  disabled={copyLoading || !currentRate}
                  title={!currentRate ? 'Exchange rate required' : 'Copy all records from previous month to current month'}
                >
                  <FontAwesomeIcon icon={faCopy} className="me-2" />
                  {copyLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSync} spin className="me-1" />
                      Copying...
                    </>
                  ) : (
                    'Copy from Previous Month'
                  )}
                </Dropdown.Item>
                {copiedRecordIds.size > 0 && (
                  <Dropdown.Item 
                    onClick={clearCopiedHighlighting}
                    className="text-muted"
                  >
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    Clear Highlighting ({copiedRecordIds.size} records)
                  </Dropdown.Item>
                )}
                <Dropdown.Divider />
                <Dropdown.Item disabled className="text-muted small">
                  More actions coming soon...
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            
            {/* Bulk Delete Button */}
            {selectedRecordIds.size > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteLoading}
                  title={`Delete ${selectedRecordIds.size} selected record${selectedRecordIds.size > 1 ? 's' : ''}`}
                  style={{ width: '100%' }}
                >
                  {bulkDeleteLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSync} spin className="me-1" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTrashAlt} className="me-1" />
                      Delete Selected ({selectedRecordIds.size})
                    </>
                  )}
                </button>
                
                <button
                  className="btn btn-warning btn-sm"
                  onClick={handleBulkSetZeroAmount}
                  disabled={bulkDeleteLoading}
                  title={`Set zero amounts for ${selectedRecordIds.size} selected record${selectedRecordIds.size > 1 ? 's' : ''}`}
                  style={{ width: '100%' }}
                >
                  {bulkDeleteLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSync} spin className="me-1" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCoins} className="me-1" />
                      Set Zero Amount ({selectedRecordIds.size})
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add New Record Form */}
      <div className="add-record-section">
        <div className="record-form-compact">
          <div className="form-row-main">
            <div className="form-group" style={{ minWidth: '100px' }}>
              <label>Type:</label>
              <select 
                className={`form-control form-select record-type-select ${formData.recordType === 'income' ? 'income-selected' : ''}`}
                value={formData.recordType}
                onChange={(e) => setFormData({...formData, recordType: e.target.value as RecordType})}
                title="Select record type"
                onKeyPress={handleKeyPress}
                style={{ height: '36px', padding: '0 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', backgroundColor: 'white', color: '#374151', width: '100%' }}
              >
                <option value="expense">Expense</option>
                <option value="income" className="income-option">Income</option>
              </select>
            </div>
            
            <div className="form-group date-group">
              <label>Date:</label>
              <input 
                type="date" 
                className="form-control compact-input" 
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                onKeyPress={handleKeyPress}
                title="Record date"
                style={{ height: '36px', padding: '9px 3px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', backgroundColor: 'white', color: '#374151', fontFamily: 'inherit', lineHeight: 'normal' }}
              />
            </div>
            
            <div className="form-group description-group">
              <label>Description:</label>
              <div className="description-input-container">
                <input 
                  ref={descriptionInputRef}
                  type="text" 
                  className="form-control" 
                  value={formData.description}
                  onChange={handleDescriptionInputChange}
                  onBlur={handleDescriptionBlur}
                  title="Record description (auto-parsing enabled)" 
                  onKeyPress={handleKeyPress}
                  placeholder=""
                  style={{ height: '36px', padding: '0 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', backgroundColor: 'white', color: '#374151' }}
                />
                {isLLMLoading && (
                  <div className="llm-parsing-indicator">
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                )}
                {!isLLMLoading && formData.description.trim() && isLLMServiceAvailable() && (
                  <button
                    type="button"
                    className="manual-parse-btn"
                    onClick={handleManualParse}
                    title="Parse description with AI"
                  >
                    <img 
                      src="/images/ai-icon.png" 
                      alt="AI" 
                      className="ai-icon"
                    />
                  </button>
                )}
              </div>
            </div>
            
            <div className="form-group category-group">
              <label>Category:</label>
              <select 
                className="form-control form-select compact-select" 
                value={formData.categoryId}
                onChange={(e) => setFormData({...formData, categoryId: e.target.value})}
                title="Select category"
                onKeyPress={handleKeyPress}
                style={{ height: '36px', padding: '0 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', backgroundColor: 'white', color: '#374151' }}
              >
                <option value="">Select category...</option>
                {Array.isArray(categories) && categories
                  .filter(cat => cat.type === formData.recordType && cat.account_type === formData.accountType)
                  .map(category => (
                    <option key={category.id} value={category.id} title={category.description || ''}>
                      {category.name}
                    </option>
                  ))}
              </select>
            </div>
            
            <div className="form-group amount-group-vnd">
              <label>VND:</label>
              <input 
                type="text" 
                className={`form-control amount-input-vnd ${formulaErrors.amountVnd ? 'formula-error' : ''}`}
                value={formData.amountVnd}
                onChange={handleAmountVndChange}
                onFocus={(e) => handleFocus('amountVnd', e)}
                onBlur={handleAmountVndBlur}
                onKeyPress={handleKeyPress}
                onKeyDown={(e) => handleKeyDown(e, 'amountVnd')}
                onDoubleClick={() => handleDoubleClick('amountVnd')}
                title="Amount in Vietnamese Dong (Press Ctrl+Space for calculator, use =formula, or double-click to recalculate USD)"
                style={{ height: '36px', padding: '0 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', backgroundColor: 'white', color: '#374151' }}
              />
            </div>
            
            <div className="form-group amount-group">
              <label>USD:</label>
              <input 
                type="text" 
                className={`form-control amount-input-usd ${formulaErrors.amountUsd ? 'formula-error' : ''}`}
                value={formData.amountUsd}
                onChange={handleAmountUsdChange}
                onFocus={(e) => handleFocus('amountUsd', e)}
                onBlur={handleAmountUsdBlur}
                onKeyPress={handleKeyPress}
                onKeyDown={(e) => handleKeyDown(e, 'amountUsd')}
                onDoubleClick={() => handleDoubleClick('amountUsd')}
                title="Amount in US Dollars (e.g., 1.5, 2.0) (Press Ctrl+Space for calculator, use =formula, or double-click to recalculate VND)"
                placeholder="0.0"
                style={{ height: '36px', padding: '0 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', backgroundColor: 'white', color: '#374151' }}
              />
            </div>
            
            <div className="form-group tax-group">
              <label>Tax (%):</label>
              <input 
                type="text" 
                className="form-control tax-percent-input" 
                value={formData.taxPercent}
                onChange={handleTaxPercentChangeWithVisibility}
                onFocus={(e) => handleFocus('taxPercent', e)}
                onBlur={handleTaxPercentBlur}
                onKeyPress={handleKeyPress}
                onDoubleClick={() => handleDoubleClick('taxPercent')}
                title="Tax percentage (double-click to recalculate tax amounts)"
                style={{ height: '36px', padding: '0 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', backgroundColor: 'white', color: '#374151' }}
              />
            </div>
            
            <div className={`form-group tax-group-vnd ${!shouldShowTaxAmounts() ? 'tax-amount-hidden' : ''}`}>
              <label>Tax (VND):</label>
              <input 
                type="text" 
                className={`form-control tax-input-vnd ${formulaErrors.taxVnd ? 'formula-error' : ''}`}
                value={formData.taxVnd}
                onChange={handleTaxVndChange}
                onFocus={(e) => handleFocus('taxVnd', e)}
                onBlur={handleTaxVndBlur}
                onKeyPress={handleKeyPress}
                onKeyDown={(e) => handleKeyDown(e, 'taxVnd')}
                onDoubleClick={() => handleDoubleClick('taxVnd')}
                title="Tax amount in Vietnamese Dong (Press Ctrl+Space for calculator, use =formula, or double-click to recalculate tax %)"
                style={{ height: '36px', padding: '0 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', backgroundColor: 'white', color: '#374151' }}
              />
            </div>
            
            <div className={`form-group tax-group ${!shouldShowTaxAmounts() ? 'tax-amount-hidden' : ''}`}>
              <label>Tax (USD):</label>
              <input 
                type="text" 
                className={`form-control tax-input-usd ${formulaErrors.taxUsd ? 'formula-error' : ''}`}
                value={formData.taxUsd}
                onChange={handleTaxUsdChange}
                onFocus={(e) => handleFocus('taxUsd', e)}
                onBlur={handleTaxUsdBlur}
                onKeyPress={handleKeyPress}
                onKeyDown={(e) => handleKeyDown(e, 'taxUsd')}
                onDoubleClick={() => handleDoubleClick('taxUsd')}
                title="Tax amount in US Dollars (e.g., 1.5, 2.0) (Press Ctrl+Space for calculator, use =formula, or double-click to recalculate tax %)"
                placeholder="0.0"
                style={{ height: '36px', padding: '0 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', backgroundColor: 'white', color: '#374151' }}
              />
            </div>
            
            <div className="form-group save-group">
              {editingRecord && (
                <Button 
                  variant="outline-secondary" 
                  className="btn-cancel" 
                  onClick={handleCancelEdit} 
                  title="Cancel editing and reset form"
                >
                  <FontAwesomeIcon icon={faTimes} /> Cancel
                </Button>
              )}
              <Button variant="primary" className="btn-save" onClick={handleFormSave}>
                <FontAwesomeIcon icon={faSave} /> {editingRecord ? 'Update' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Records Table with Filters */}
      <div className="records-table-section">
        <RecordFilters 
          filters={filters} 
          categories={categories} 
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onResetFilters={handleResetFilters}
        />

        {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading records...</p>
        </div>
      ) : (
        <>
          {/* Compact Stats Summary - Similar to Header Panel */}
          <div className="records-stats-panel" style={{ display: 'flex', gap: 12, background: 'white', border: 'none', borderRadius: 8, padding: 0, marginBottom: 'var(--spacing-1)', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', alignItems: 'stretch', minHeight: 64 }}>
                        {/* Balance */}
            <div className="stats-section balance-stats" style={{ flex: 1, minWidth: 140, minHeight: 64, display: 'flex', flexDirection: 'column', justifyContent: 'center', border: '1px solid #3b82f6', borderRadius: 8, background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white', padding: '8px 12px', boxSizing: 'border-box' }}>
              <div className="stats-header" style={{ marginBottom: 2, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Balance</div>
              <div className="stats-display" style={{ gap: 0 }}>
                <div className="stats-primary" style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1 }}>
                  {(() => {
                    // Use actual records data for accurate calculation
                    const incomeRecords = records.filter(r => r.record_type === 'income');
                    const expenseRecords = records.filter(r => r.record_type === 'expense');
                    
                    const totalIncomeVnd = incomeRecords.reduce((sum, r) => sum + Math.abs(r.amount_vnd) + Math.abs(r.tax_amount_vnd), 0);
                    const totalExpenseVnd = expenseRecords.reduce((sum, r) => sum + Math.abs(r.amount_vnd) + Math.abs(r.tax_amount_vnd), 0);
                    const balanceVnd = totalIncomeVnd - totalExpenseVnd;
                    return formatCurrency(balanceVnd, 'vnd');
                  })()}
            </div>
                <div className="stats-secondary" style={{ fontSize: 10, fontWeight: 500, lineHeight: 1.1, opacity: 0.9 }}>
                  {(() => {
                    // Use actual USD amounts from records (not recalculated)
                    const incomeRecords = records.filter(r => r.record_type === 'income');
                    const expenseRecords = records.filter(r => r.record_type === 'expense');
                    
                    const totalIncomeUsd = incomeRecords.reduce((sum, r) => sum + Math.abs(r.amount_usd) + Math.abs(r.tax_amount_usd), 0);
                    const totalExpenseUsd = expenseRecords.reduce((sum, r) => sum + Math.abs(r.amount_usd) + Math.abs(r.tax_amount_usd), 0);
                    const balanceUsd = totalIncomeUsd - totalExpenseUsd;
                    return formatCurrency(balanceUsd, 'usd');
                  })()}
            </div>
            </div>
            </div>

            {/* Income WT Tax */}
            <div className="stats-section income-wt-tax-stats" style={{ flex: 1, minWidth: 120, minHeight: 64, display: 'flex', flexDirection: 'column', justifyContent: 'center', border: '1px solid #22c55e', borderRadius: 8, background: '#f0fdf4', padding: '8px 12px', boxSizing: 'border-box' }}>
              <div className="stats-header" style={{ marginBottom: 2, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#166534' }}>Income WT Tax</div>
              <div className="stats-display" style={{ gap: 0 }}>
                <div className="stats-primary" style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1, color: '#166534' }}>
                  {(() => {
                    // Calculate income without tax amount (WT = without tax)
                    const incomeRecords = records.filter(r => r.record_type === 'income');
                    const totalIncomeWtVnd = incomeRecords.reduce((sum, r) => sum + Number(r.amount_vnd), 0);
                    return formatCurrency(totalIncomeWtVnd, 'vnd');
                  })()}
                </div>
                <div className="stats-secondary" style={{ fontSize: 10, fontWeight: 500, lineHeight: 1.1, color: '#16a34a' }}>
                  {(() => {
                    // Calculate income USD without tax amount
                    const incomeRecords = records.filter(r => r.record_type === 'income');
                    const totalIncomeWtUsd = incomeRecords.reduce((sum, r) => sum + Number(r.amount_usd), 0);
                    return formatCurrency(totalIncomeWtUsd, 'usd');
                  })()}
                </div>
              </div>
            </div>

            {/* Income Tax */}
            <div className="stats-section income-tax-stats" style={{ flex: 1, minWidth: 110, minHeight: 64, display: 'flex', flexDirection: 'column', justifyContent: 'center', border: '1px solid #14b8a6', borderRadius: 8, background: '#f0fdfa', padding: '8px 12px', boxSizing: 'border-box' }}>
              <div className="stats-header" style={{ marginBottom: 2, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#134e4a' }}>Income Tax</div>
              <div className="stats-display" style={{ gap: 0 }}>
                <div className="stats-primary" style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1, color: '#134e4a' }}>
                  {(() => {
                    // Calculate income tax total to match summary row
                    const incomeRecords = records.filter(r => r.record_type === 'income');
                    const totalIncomeTaxVnd = incomeRecords.reduce((sum, r) => sum + Number(r.tax_amount_vnd), 0);
                    return formatCurrency(totalIncomeTaxVnd, 'vnd');
                  })()}
                </div>
                <div className="stats-secondary" style={{ fontSize: 10, fontWeight: 500, lineHeight: 1.1, color: '#0d9488' }}>
                  {(() => {
                    // Calculate income tax USD total to match summary row
                    const incomeRecords = records.filter(r => r.record_type === 'income');
                    const totalIncomeTaxUsd = incomeRecords.reduce((sum, r) => sum + Number(r.tax_amount_usd), 0);
                    return formatCurrency(totalIncomeTaxUsd, 'usd');
                  })()}
                </div>
              </div>
            </div>

            {/* Income */}
            <div className="stats-section income-stats" style={{ flex: 1, minWidth: 110, minHeight: 64, display: 'flex', flexDirection: 'column', justifyContent: 'center', border: '1px solid #10b981', borderRadius: 8, background: '#ecfdf5', padding: '8px 12px', boxSizing: 'border-box' }}>
              <div className="stats-header" style={{ marginBottom: 2, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#065f46' }}>Income</div>
              <div className="stats-display" style={{ gap: 0 }}>
                <div className="stats-primary" style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1, color: '#065f46' }}>
                  {(() => {
                    // Calculate income total (amount + tax) to match summary row
                    const incomeRecords = records.filter(r => r.record_type === 'income');
                    const totalIncomeVnd = incomeRecords.reduce((sum, r) => sum + (Number(r.amount_vnd) + Number(r.tax_amount_vnd)), 0);
                    return formatCurrency(totalIncomeVnd, 'vnd');
                  })()}
                </div>
                <div className="stats-secondary" style={{ fontSize: 10, fontWeight: 500, lineHeight: 1.1, color: '#059669' }}>
                  {(() => {
                    // Calculate income USD total (amount + tax) to match summary row
                    const incomeRecords = records.filter(r => r.record_type === 'income');
                    const totalIncomeUsd = incomeRecords.reduce((sum, r) => sum + (Number(r.amount_usd) + Number(r.tax_amount_usd)), 0);
                    return formatCurrency(totalIncomeUsd, 'usd');
                  })()}
                </div>
              </div>
            </div>

            {/* Expense */}
            <div className="stats-section expense-stats" style={{ flex: 1, minWidth: 120, minHeight: 64, display: 'flex', flexDirection: 'column', justifyContent: 'center', border: '1px solid #ef4444', borderRadius: 8, background: '#fef2f2', padding: '8px 12px', boxSizing: 'border-box' }}>
              <div className="stats-header" style={{ marginBottom: 2, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#991b1b' }}>Expense</div>
              <div className="stats-display" style={{ gap: 0 }}>
                <div className="stats-primary" style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1, color: '#991b1b' }}>
                  {(() => {
                    // Calculate expense total to match summary row
                    const expenseRecords = records.filter(r => r.record_type === 'expense');
                    const totalExpenseVnd = expenseRecords.reduce((sum, r) => sum + (Number(r.amount_vnd) + Number(r.tax_amount_vnd)), 0);
                    return formatCurrency(totalExpenseVnd, 'vnd');
                  })()}
                </div>
                <div className="stats-secondary" style={{ fontSize: 10, fontWeight: 500, lineHeight: 1.1, color: '#dc2626' }}>
                  {(() => {
                    // Calculate expense USD total to match summary row
                    const expenseRecords = records.filter(r => r.record_type === 'expense');
                    const totalExpenseUsd = expenseRecords.reduce((sum, r) => sum + (Number(r.amount_usd) + Number(r.tax_amount_usd)), 0);
                    return formatCurrency(totalExpenseUsd, 'usd');
                  })()}
                </div>
              </div>
            </div>

            {/* Expense Tax */}
            <div className="stats-section expense-tax-stats" style={{ flex: 1, minWidth: 110, minHeight: 64, display: 'flex', flexDirection: 'column', justifyContent: 'center', border: '1px solid #f97316', borderRadius: 8, background: '#fff7ed', padding: '8px 12px', boxSizing: 'border-box' }}>
              <div className="stats-header" style={{ marginBottom: 2, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9a3412' }}>Expense Tax</div>
              <div className="stats-display" style={{ gap: 0 }}>
                <div className="stats-primary" style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1, color: '#9a3412' }}>
                  {(() => {
                    // Calculate expense tax total to match summary row
                    const expenseRecords = records.filter(r => r.record_type === 'expense');
                    const totalExpenseTaxVnd = expenseRecords.reduce((sum, r) => sum + Number(r.tax_amount_vnd), 0);
                    return formatCurrency(totalExpenseTaxVnd, 'vnd');
                  })()}
                </div>
                <div className="stats-secondary" style={{ fontSize: 10, fontWeight: 500, lineHeight: 1.1, color: '#ea580c' }}>
                  {(() => {
                    // Calculate expense tax USD total to match summary row
                    const expenseRecords = records.filter(r => r.record_type === 'expense');
                    const totalExpenseTaxUsd = expenseRecords.reduce((sum, r) => sum + Number(r.tax_amount_usd), 0);
                    return formatCurrency(totalExpenseTaxUsd, 'usd');
                  })()}
                </div>
              </div>
            </div>

            {/* Expense WT Tax */}
            <div className="stats-section expense-wt-tax-stats" style={{ flex: 1, minWidth: 110, minHeight: 64, display: 'flex', flexDirection: 'column', justifyContent: 'center', border: '1px solid #f43f5e', borderRadius: 8, background: '#fff1f2', padding: '8px 12px', boxSizing: 'border-box' }}>
              <div className="stats-header" style={{ marginBottom: 2, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9f1239' }}>Expense WT Tax</div>
              <div className="stats-display" style={{ gap: 0 }}>
                <div className="stats-primary" style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1, color: '#9f1239' }}>
                  {(() => {
                    // Calculate expense without tax amount (WT = without tax)
                    const expenseRecords = records.filter(r => r.record_type === 'expense');
                    const totalExpenseVnd = expenseRecords.reduce((sum, r) => sum + Number(r.amount_vnd), 0);
                    return formatCurrency(totalExpenseVnd, 'vnd');
                  })()}
                </div>
                <div className="stats-secondary" style={{ fontSize: 10, fontWeight: 500, lineHeight: 1.1, color: '#e11d48' }}>
                  {(() => {
                    // Calculate expense USD without tax amount
                    const expenseRecords = records.filter(r => r.record_type === 'expense');
                    const totalExpenseUsd = expenseRecords.reduce((sum, r) => sum + Number(r.amount_usd), 0);
                    return formatCurrency(totalExpenseUsd, 'usd');
                  })()}
                </div>
              </div>
            </div>
          </div>

          <div className="records-content">
            <div className="table-container">
              <Table responsive hover className="records-table">
              <thead>
                <tr>
                  <th style={{ width: '45px', textAlign: 'center', padding: '8px' }}>
                    #
                  </th>
                  <th style={{ width: '40px', textAlign: 'center', padding: '8px' }}>
                    <input
                      type="checkbox"
                      checked={selectedRecordIds.size === records.length && records.length > 0}
                      onChange={handleToggleSelectAll}
                      title="Select all records"
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th 
                    onClick={() => handleSort('record_id')} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by ID"
                  >
                    ID{getSortIndicator('record_id')}
                  </th>
                  <th 
                    onClick={() => handleSort('record_date')} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by Date"
                  >
                    Date{getSortIndicator('record_date')}
                  </th>
                  <th 
                    onClick={() => handleSort('description')} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by Description"
                  >
                    Description{getSortIndicator('description')}
                  </th>
                  <th 
                    onClick={() => handleSort('category_id')} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by Category"
                  >
                    Category{getSortIndicator('category_id')}
                  </th>
                  <th 
                    onClick={() => handleSort('amount_vnd')} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by Amount"
                  >
                    AMOUNT{getSortIndicator('amount_vnd')}
                  </th>
                  <th 
                    onClick={() => handleSort('tax_percent')} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by Tax %"
                  >
                    Tax %{getSortIndicator('tax_percent')}
                  </th>
                  <th 
                    onClick={() => handleSort('tax_amount_vnd')} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by Tax Amount"
                  >
                    TAX AMOUNT{getSortIndicator('tax_amount_vnd')}
                  </th>
                  <th>Exch. Rate</th>
                  <th 
                    onClick={() => handleSort('report_amount_vnd')} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by Report Amount"
                  >
                    REPORT AMOUNT{getSortIndicator('report_amount_vnd')}
                  </th>
                  <th className="actions-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.length > 0 ? (
                  records.map((record, index) => (
                    <tr key={record.id} className={`
                      ${record.record_type === 'income' ? 'income-row' : 'expense-row'}
                      ${editingRecord && editingRecord.id === record.id ? 'editing-row' : ''}
                      ${copiedRecordIds.has(record.id) ? 'copied-record' : ''}
                    `.trim()}>
                      <td style={{ textAlign: 'center', padding: '8px', fontSize: '0.85rem', color: '#6b7280' }}>
                        {index + 1}
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px' }}>
                        <input
                          type="checkbox"
                          checked={selectedRecordIds.has(record.id)}
                          onChange={() => handleToggleSelectRecord(record.id)}
                          title="Select this record"
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td>
                        {record.record_id}
                        {copiedRecordIds.has(record.id) && (
                          <span className="copied-indicator" title="Recently copied record">
                            <FontAwesomeIcon icon={faCopy} />
                          </span>
                        )}
                      </td>
                      <td>{format(new Date(record.record_date), 'MM/dd/yyyy')}</td>
                      <td>{record.description}</td>
                      <td>
                        <span className={`category-text ${record.record_type}`}>
                          {record.Category?.name}
                        </span>
                      </td>
                      <td className="amount">
                        {formatAmountCell(record.amount_vnd, record.amount_usd)}
                      </td>
                      <td className="tax-percent">{record.tax_percent > 0 ? `${record.tax_percent}%` : ''}</td>
                      <td className="amount">
                        {formatTaxAmountCell(record.tax_amount_vnd, record.tax_amount_usd)}
                      </td>
                      <td className="exchange-rate">
                        {record.ExchangeRate ? formatNumber(record.ExchangeRate.vnd_to_usd) : 'N/A'}
                      </td>
                      <td className="total-amount">
                        <ReportAmountCell record={record} />
                      </td>
                      <td>
                        <div className="record-actions">
                          <Button 
                            variant="outline-primary"
                            size="sm"
                            className="btn-icon edit" 
                            onClick={() => handleEditRecord(record)} 
                            title="Edit record"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button 
                            variant="outline-success"
                            size="sm"
                            className="btn-icon duplicate" 
                            onClick={() => handleDuplicateRecord(record)} 
                            title="Duplicate record"
                          >
                            <FontAwesomeIcon icon={faClone} />
                          </Button>
                          <Button 
                            variant="outline-danger"
                            size="sm"
                            className="btn-icon delete" 
                            onClick={() => handleDeleteRecord(record)}
                            title="Delete record"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="no-records">
                      No records found. Adjust filters or add a new record.
                    </td>
                  </tr>
                )}
              </tbody>
              {/* Summary totals row */}
              {records.length > 0 && (
                <tfoot>
                  <tr className="summary-totals-row">
                    <td><strong>TOTAL ({records.length})</strong></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td className="amount">
                      <strong>
                        <div className="amount-cell">
                          <div className="amount-vnd">
                            {(() => {
                              // Calculate total VND amount
                              let totalVnd = 0;
                              records.forEach(record => {
                                const amount = Number(record.amount_vnd) || 0;
                                totalVnd += amount;
                              });
                              return formatCurrency(totalVnd, 'vnd');
                            })()}
                          </div>
                          <div className="amount-usd">
                            {(() => {
                              // Calculate total USD amount
                              let totalUsd = 0;
                              records.forEach(record => {
                                const amount = Number(record.amount_usd) || 0;
                                totalUsd += amount;
                              });
                              return formatCurrency(totalUsd, 'usd');
                            })()}
                          </div>
                        </div>
                      </strong>
                    </td>
                    <td></td>
                    <td className="amount">
                      <strong>
                        <div className="tax-amount-cell">
                          <div className="tax-vnd">
                            {(() => {
                              // Calculate total tax VND
                              let totalTaxVnd = 0;
                              records.forEach(record => {
                                const amount = Number(record.tax_amount_vnd) || 0;
                                totalTaxVnd += amount;
                              });
                              return totalTaxVnd !== 0 ? formatCurrency(totalTaxVnd, 'vnd') : '';
                            })()}
                          </div>
                          <div className="tax-usd">
                            {(() => {
                              // Calculate total tax USD
                              let totalTaxUsd = 0;
                              records.forEach(record => {
                                const amount = Number(record.tax_amount_usd) || 0;
                                totalTaxUsd += amount;
                              });
                              return totalTaxUsd !== 0 ? formatCurrency(totalTaxUsd, 'usd') : '';
                            })()}
                          </div>
                        </div>
                      </strong>
                    </td>
                    <td></td>
                    <td className="total-amount">
                      <strong>
                        <div className="total-amount-cell">
                          <div className="total-vnd">
                            {(() => {
                              // Calculate total report amount VND
                              let totalReportVnd = 0;
                              records.forEach(record => {
                                const amount = Number(record.amount_vnd) || 0;
                                const tax = Number(record.tax_amount_vnd) || 0;
                                totalReportVnd += (amount + tax);
                              });
                              return formatCurrency(totalReportVnd, 'vnd');
                            })()}
                          </div>
                          <div className="total-usd">
                            {(() => {
                              // Calculate total report amount USD
                              let totalReportUsd = 0;
                              records.forEach(record => {
                                const amount = Number(record.amount_usd) || 0;
                                const tax = Number(record.tax_amount_usd) || 0;
                                totalReportUsd += (amount + tax);
                              });
                              return formatCurrency(totalReportUsd, 'usd');
                            })()}
                          </div>
                        </div>
                      </strong>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </Table>
            </div>
          </div>

        </>
      )}
      </div>

      {/* Category Summary Table */}
      <div className="category-summary-section">
        <h3 className="section-title">
          <FontAwesomeIcon icon={faChartPie} />
          Category Summary
        </h3>
        
        {summaryLoading ? (
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>Loading category summary...</p>
          </div>
        ) : (
          <div className="summary-content">
            <div className="summary-table-container">
              <Table responsive hover className="category-summary-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Count</th>
                    <th>AMOUNT</th>
                    <th>TAX AMOUNT</th>
                    <th>REPORT AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  {categorySummary.length > 0 ? (
                    categorySummary.map((summary) => (
                      <tr key={`${summary.category_id}-${summary.record_type}`} className={`
                        ${summary.record_type === 'income' ? 'income-row' : 'expense-row'}
                      `.trim()}>
                        <td>
                          <span className={`category-text ${summary.record_type}`}>
                            {summary.Category?.name || 'Unknown'}
                          </span>
                        </td>
                        <td>
                          <Badge bg={summary.record_type === 'income' ? 'success' : 'danger'}>
                            {summary.record_type.charAt(0).toUpperCase() + summary.record_type.slice(1)}
                          </Badge>
                        </td>
                        <td>{summary.record_count}</td>
                        <td className="amount">
                          {formatAmountCell(summary.total_amount_vnd || summary.total_amount, summary.total_amount_usd || (summary.total_amount / (currentRate?.vnd_to_usd || 25000)))}
                        </td>
                        <td className="amount">
                          {formatTaxAmountCell(summary.total_tax_vnd || summary.total_tax, summary.total_tax_usd || (summary.total_tax / (currentRate?.vnd_to_usd || 25000)))}
                        </td>
                        <td className="total-amount">
                          <div className="total-amount-cell">
                            <div className="total-vnd">
                              {formatCurrency(summary.total_report_vnd || ((summary.total_amount_vnd || 0) + (summary.total_tax_vnd || 0)), 'vnd')}
                            </div>
                            <div className="total-usd">
                              {formatCurrency(summary.total_report_usd || ((summary.total_amount_usd || 0) + (summary.total_tax_usd || 0)), 'usd')}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="no-records">
                        No category summary data available for the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
                {/* Summary totals row */}
                {categorySummary.length > 0 && (
                  <tfoot>
                    <tr className="summary-totals-row">
                      <td><strong>TOTAL</strong></td>
                      <td></td>
                      <td><strong>{categorySummary.reduce((sum, item) => sum + item.record_count, 0)}</strong></td>
                      <td className="amount">
                        <strong>
                          <div className="amount-cell">
                            <div className="amount-vnd">
                              {(() => {
                                // Calculate total VND amount
                                let totalVnd = 0;
                                categorySummary.forEach(item => {
                                  const amount = item.total_amount_vnd || item.total_amount || 0;
                                  if (item.record_type === 'expense') {
                                    totalVnd -= Math.abs(amount);
                                  } else {
                                    totalVnd += Math.abs(amount);
                                  }
                                });
                                return formatCurrency(totalVnd, 'vnd');
                              })()}
                            </div>
                            <div className="amount-usd">
                              {(() => {
                                // Calculate total USD amount
                                let totalUsd = 0;
                                categorySummary.forEach(item => {
                                  const amount = item.total_amount_usd || ((item.total_amount || 0) / (currentRate?.vnd_to_usd || 25000));
                                  if (item.record_type === 'expense') {
                                    totalUsd -= Math.abs(amount);
                                  } else {
                                    totalUsd += Math.abs(amount);
                                  }
                                });
                                return formatCurrency(totalUsd, 'usd');
                              })()}
                            </div>
                          </div>
                        </strong>
                      </td>
                      <td className="amount">
                        <strong>
                          <div className="tax-amount-cell">
                            <div className="tax-vnd">
                              {(() => {
                                // Calculate total tax VND
                                let totalTaxVnd = 0;
                                categorySummary.forEach(item => {
                                  const taxAmount = item.total_tax_vnd || item.total_tax || 0;
                                  if (item.record_type === 'expense') {
                                    totalTaxVnd -= Math.abs(taxAmount);
                                  } else {
                                    totalTaxVnd += Math.abs(taxAmount);
                                  }
                                });
                                return totalTaxVnd !== 0 ? formatCurrency(totalTaxVnd, 'vnd') : '';
                              })()}
                            </div>
                            <div className="tax-usd">
                              {(() => {
                                // Calculate total tax USD
                                let totalTaxUsd = 0;
                                categorySummary.forEach(item => {
                                  const taxAmount = item.total_tax_usd || ((item.total_tax || 0) / (currentRate?.vnd_to_usd || 25000));
                                  if (item.record_type === 'expense') {
                                    totalTaxUsd -= Math.abs(taxAmount);
                                  } else {
                                    totalTaxUsd += Math.abs(taxAmount);
                                  }
                                });
                                return totalTaxUsd !== 0 ? formatCurrency(totalTaxUsd, 'usd') : '';
                              })()}
                            </div>
                          </div>
                        </strong>
                      </td>
                      <td className="total-amount">
                        <strong>
                          <div className="total-amount-cell">
                            <div className="total-vnd">
                              {(() => {
                                // Calculate total report amount VND using the new total_report_vnd field
                                let totalReportVnd = 0;
                                categorySummary.forEach(item => {
                                  // Use the total_report_vnd field directly which is already calculated as amount + tax
                                  const reportAmount = item.total_report_vnd || 0;
                                  if (item.record_type === 'expense') {
                                    totalReportVnd -= Math.abs(reportAmount);
                                  } else {
                                    totalReportVnd += Math.abs(reportAmount);
                                  }
                                });
                                return formatCurrency(totalReportVnd, 'vnd');
                              })()}
                            </div>
                            <div className="total-usd">
                              {(() => {
                                // Calculate total report amount USD using the new total_report_usd field
                                let totalReportUsd = 0;
                                categorySummary.forEach(item => {
                                  // Use the total_report_usd field directly which is already calculated as amount + tax
                                  const reportAmount = item.total_report_usd || 0;
                                  if (item.record_type === 'expense') {
                                    totalReportUsd -= Math.abs(reportAmount);
                                  } else {
                                    totalReportUsd += Math.abs(reportAmount);
                                  }
                                });
                                return formatCurrency(totalReportUsd, 'usd');
                              })()}
                            </div>
                          </div>
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </Table>
            </div>
            
            <div className="summary-chart-container">
              {records.length > 0 ? (
                <div className="charts-grid">
                  {/* Income vs Expense Comparison Chart */}
                  <div className="pie-chart">
                    <h4>Income vs Expense</h4>
                    {/* Recharts PieChart for Income vs Expense */}
                    <ResponsiveContainer width={180} height={180}>
                      <PieChart>
                        <Pie
                          data={(() => {
                            const incomeItems = categorySummary.filter(item => item.record_type === 'income');
                            const expenseItems = categorySummary.filter(item => item.record_type === 'expense');
                            
                            const incomeTotal = incomeItems.reduce((sum, item) => sum + Math.abs((item.total_amount_vnd || item.total_amount) + (item.total_tax_vnd || item.total_tax)), 0);
                            const expenseTotal = expenseItems.reduce((sum, item) => sum + Math.abs((item.total_amount_vnd || item.total_amount) + (item.total_tax_vnd || item.total_tax)), 0);
                            
                            return [
                              { name: 'Income', value: incomeTotal },
                              { name: 'Expense', value: expenseTotal }
                            ];
                          })()}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={70}
                        >
                          <Cell key="income" fill="#10b981" />
                          <Cell key="expense" fill="#ef4444" />
                        </Pie>
                        <Tooltip 
                          formatter={(value: number) => value.toLocaleString('en-US')}
                          contentStyle={{ fontSize: '11px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="chart-legend">
                      {(() => {
                        const incomeItems = categorySummary.filter(item => item.record_type === 'income');
                        const expenseItems = categorySummary.filter(item => item.record_type === 'expense');
                        
                        const incomeTotal = incomeItems.reduce((sum, item) => sum + Math.abs((item.total_amount_usd || (item.total_amount / (currentRate?.vnd_to_usd || 25000))) + (item.total_tax_usd || (item.total_tax / (currentRate?.vnd_to_usd || 25000)))), 0);
                        const expenseTotal = expenseItems.reduce((sum, item) => sum + Math.abs((item.total_amount_usd || (item.total_amount / (currentRate?.vnd_to_usd || 25000))) + (item.total_tax_usd || (item.total_tax / (currentRate?.vnd_to_usd || 25000)))), 0);
                        
                        return (
                          <>
                            {incomeTotal > 0 && (
                              <div className="legend-item">
                                <div className="legend-color" style={{ backgroundColor: '#10b981' }}>
                                  <FontAwesomeIcon icon={faMoneyBillWave} className="legend-icon" />
                                </div>
                                <span className="legend-text">
                                  Income - {(() => {
                                    const totalAmount = incomeTotal + expenseTotal;
                                    return totalAmount > 0 ? ((incomeTotal / totalAmount) * 100).toFixed(1) : '0.0';
                                  })()}%<br/>
                                  ${incomeTotal.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                </span>
                              </div>
                            )}
                            {expenseTotal > 0 && (
                              <div className="legend-item">
                                <div className="legend-color" style={{ backgroundColor: '#ef4444' }}>
                                  <FontAwesomeIcon icon={faCoins} className="legend-icon" />
                                </div>
                                <span className="legend-text">
                                  Expense - {(() => {
                                    const totalAmount = incomeTotal + expenseTotal;
                                    return totalAmount > 0 ? ((expenseTotal / totalAmount) * 100).toFixed(1) : '0.0';
                                  })()}%<br/>
                                  ${expenseTotal.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                </span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Income Categories Chart */}
                  {categorySummary.filter(item => item.record_type === 'income').length > 0 && (
                    <div className="pie-chart">
                      <h4>Income by Category</h4>
                      <ResponsiveContainer width={180} height={180}>
                        <PieChart>
                          <Pie
                            data={(() => {
                              const incomeItems = categorySummary.filter(item => item.record_type === 'income');
                              
                              return incomeItems.map(item => {
                                const value = Math.abs((item.total_amount_vnd || item.total_amount) + (item.total_tax_vnd || item.total_tax));
                                return {
                                  name: item.Category?.name || 'Unknown',
                                  value: value,
                                  categoryId: item.category_id
                                };
                              });
                            })()}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                          >
                            {(() => {
                              const colors = ['#10b981', '#06b6d4', '#84cc16', '#f59e0b', '#8b5cf6'];
                              const incomeItems = categorySummary.filter(item => item.record_type === 'income');
                              
                              return incomeItems.map((item, index) => (
                                <Cell 
                                  key={`income-cell-${item.category_id}`} 
                                  fill={colors[index % colors.length]} 
                                />
                              ));
                            })()}
                          </Pie>
                          <Tooltip 
                            formatter={(value: number) => value.toLocaleString('en-US')} 
                            contentStyle={{ fontSize: '11px' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="chart-legend">
                        {categorySummary.filter(item => item.record_type === 'income').map((item, index) => {
                          const incomeItems = categorySummary.filter(item => item.record_type === 'income');
                          const total = Math.abs(incomeItems.reduce((sum, item) => sum + Math.abs((item.total_amount_vnd || item.total_amount) + (item.total_tax_vnd || item.total_tax)), 0));
                          const value = Math.abs((item.total_amount_vnd || item.total_amount) + (item.total_tax_vnd || item.total_tax));
                          const percentage = incomeItems.length === 1 ? 100 : (value / total) * 100;
                          const colors = ['#10b981', '#06b6d4', '#84cc16', '#f59e0b', '#8b5cf6'];
                          
                          if (percentage < 1 && incomeItems.length > 1) return null;
                          
                          return (
                            <div key={`income-legend-${item.category_id}`} className="legend-item">
                              <div 
                                className="legend-color" 
                                style={{ backgroundColor: colors[index % colors.length] }}
                              ></div>
                              <span className="legend-text">
                                <span className="category-text income">
                                  {item.Category?.name}
                                </span> - {percentage.toFixed(1)}%
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Expense Categories Chart */}
                  {categorySummary.filter(item => item.record_type === 'expense').length > 0 && (
                    <div className="pie-chart">
                      <h4>Expense by Category</h4>
                      <ResponsiveContainer width={180} height={180}>
                        <PieChart>
                          <Pie
                            data={(() => {
                              const expenseItems = categorySummary.filter(item => item.record_type === 'expense');
                              
                              return expenseItems.map(item => {
                                const value = Math.abs((item.total_amount_vnd || item.total_amount) + (item.total_tax_vnd || item.total_tax));
                                return {
                                  name: item.Category?.name || 'Unknown',
                                  value: value,
                                  categoryId: item.category_id
                                };
                              });
                            })()}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                          >
                            {(() => {
                              const colors = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#dc2626'];
                              const expenseItems = categorySummary.filter(item => item.record_type === 'expense');
                              
                              return expenseItems.map((item, index) => (
                                <Cell 
                                  key={`expense-cell-${item.category_id}`} 
                                  fill={colors[index % colors.length]} 
                                />
                              ));
                            })()}
                          </Pie>
                          <Tooltip 
                            formatter={(value: number) => value.toLocaleString('en-US')} 
                            contentStyle={{ fontSize: '11px' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="chart-legend">
                        {categorySummary.filter(item => item.record_type === 'expense').map((item, index) => {
                          const expenseItems = categorySummary.filter(item => item.record_type === 'expense');
                          const total = Math.abs(expenseItems.reduce((sum, item) => sum + Math.abs((item.total_amount_vnd || item.total_amount) + (item.total_tax_vnd || item.total_tax)), 0));
                          const value = Math.abs((item.total_amount_vnd || item.total_amount) + (item.total_tax_vnd || item.total_tax));
                          const percentage = expenseItems.length === 1 ? 100 : (value / total) * 100;
                          const colors = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#dc2626'];
                          
                          if (percentage < 1 && expenseItems.length > 1) return null;
                          
                          return (
                            <div key={`expense-legend-${item.category_id}`} className="legend-item">
                              <div 
                                className="legend-color" 
                                style={{ backgroundColor: colors[index % colors.length] }}
                              ></div>
                              <span className="legend-text">
                                <span className="category-text expense">
                                  {item.Category?.name}
                                </span> - {percentage.toFixed(1)}%
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-chart">
                  <p>No data available for charts</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <RecordForm
          record={editingRecord}
          categories={categories}
          currentRate={currentRate}
          onSave={handleSaveRecord}
          onCancel={handleCloseModal}
        />
      )}

      {/* Calculator */}
      <Calculator
        isOpen={calculatorState.isOpen}
        onClose={handleCalculatorClose}
        onResult={handleCalculatorResult}
        onTaxResult={handleTaxCalculatorResult}
        position={calculatorState.position}
      />

      {/* Exchange Rate Update Warning Modal */}
      {showExchangeRateWarning && (
        <div className="modal-overlay">
          <div className="modal-container" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f59e0b', marginRight: '8px' }} />
                Exchange Rate Update Warning
              </h3>
              <button 
                className="btn btn-sm btn-light modal-close-btn" 
                onClick={() => handleExchangeRateUpdateOption('cancel')}
                style={{ fontSize: '1.5rem', lineHeight: 1 }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <div className="alert alert-warning" style={{ marginBottom: '20px', padding: '12px 16px', borderRadius: '6px', backgroundColor: '#fef3c7', border: '1px solid #fbbf24' }}>
                <p style={{ margin: 0, fontSize: '0.95rem', color: '#92400e' }}>
                  <strong>Warning:</strong> Updating this exchange rate will affect all records that use it.
                </p>
              </div>
              
              <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '12px', fontSize: '0.95rem' }}>
                  <div style={{ fontWeight: 600, color: '#374151' }}>Exchange Rate ID:</div>
                  <div style={{ color: '#6b7280' }}>{editingExchangeRate?.id}</div>
                  
                  <div style={{ fontWeight: 600, color: '#374151' }}>Current Rate:</div>
                  <div style={{ color: '#6b7280', fontFamily: 'monospace' }}>{editingExchangeRate?.vnd_to_usd.toLocaleString()}</div>
                  
                  <div style={{ fontWeight: 600, color: '#374151' }}>New Rate:</div>
                  <div style={{ color: '#059669', fontWeight: 600, fontFamily: 'monospace' }}>{pendingExchangeRate?.toLocaleString()}</div>
                </div>
              </div>

              <p style={{ marginBottom: '16px', fontSize: '0.95rem', fontWeight: 600, color: '#374151' }}>Please select an option:</p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button 
                  className="btn" 
                  onClick={() => handleExchangeRateUpdateOption('update-rate-only')}
                  style={{ 
                    padding: '14px 18px', 
                    textAlign: 'left', 
                    border: '2px solid #3b82f6',
                    borderRadius: '8px',
                    backgroundColor: '#eff6ff',
                    color: '#1e40af',
                    transition: 'all 0.2s',
                    width: '100%',
                    display: 'block',
                    whiteSpace: 'normal',
                    lineHeight: '1.5'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#dbeafe';
                    e.currentTarget.style.borderColor = '#2563eb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#eff6ff';
                    e.currentTarget.style.borderColor = '#3b82f6';
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '6px' }}>
                    <FontAwesomeIcon icon={faExchangeAlt} style={{ marginRight: '10px', fontSize: '1rem' }} />
                    Update exchange rate only
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', paddingLeft: '28px', lineHeight: '1.4' }}>
                    Other records will keep their current USD values
                  </div>
                </button>
                
                <button 
                  className="btn" 
                  onClick={() => handleExchangeRateUpdateOption('update-records')}
                  style={{ 
                    padding: '14px 18px', 
                    textAlign: 'left',
                    borderRadius: '8px',
                    backgroundColor: '#fef3c7',
                    border: '2px solid #f59e0b',
                    color: '#92400e',
                    transition: 'all 0.2s',
                    width: '100%',
                    display: 'block',
                    whiteSpace: 'normal',
                    lineHeight: '1.5'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fde68a';
                    e.currentTarget.style.borderColor = '#d97706';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fef3c7';
                    e.currentTarget.style.borderColor = '#f59e0b';
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '6px' }}>
                    <FontAwesomeIcon icon={faSave} style={{ marginRight: '10px', fontSize: '1rem' }} />
                    Update rate and recalculate records
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#92400e', paddingLeft: '28px', lineHeight: '1.4' }}>
                    All related records will have their USD and tax amounts recalculated based on VND values
                  </div>
                </button>
                
                <button 
                  className="btn btn-secondary" 
                  onClick={() => handleExchangeRateUpdateOption('cancel')}
                  style={{ 
                    padding: '10px 16px',
                    borderRadius: '6px',
                    marginTop: '8px',
                    fontSize: '0.95rem',
                    fontWeight: 500
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification with close button */}
      {notification.show && (
        <div className={`notification ${notification.type} show`}>
          <div className="notification-content">{notification.message}</div>
          <button className="notification-close" onClick={closeNotification} title="Close notification" aria-label="Close notification">
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>
      )}
      <ImportPaymentsDialog
        mode="income"
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onSuccess={() => { fetchRecords(); fetchCurrentBalance(); }}
        categories={categories}
        accountType={formData.accountType as AccountType}
        currentRate={currentRate}
      />
      {false && showImportDialog && (
        <div className="modal-overlay" style={{ paddingLeft: '250px' }}>
          <div className="modal-container" style={{ maxWidth: 'calc(100vw - 300px)', width: '90%', minWidth: '1000px', margin: '0 auto' }}>
            <div className="modal-header">
              <h3>Import Customer Payments</h3>
              <button 
                className="btn btn-sm btn-light modal-close-btn" 
                onClick={handleImportDialogClose}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Paste Excel Data (Tab-delimited):</label>
                <p className="text-muted small mb-2">
                  Copy data from Excel without headers. Expected columns: Date, Date Time, Record ID, Description, Before Tax, After Tax, Tax Amount<br/>
                  <strong>Note:</strong> If tax columns are missing, 10% tax will be applied automatically.
                </p>
                <textarea
                  className="form-control"
                  rows={10}
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  placeholder="Paste your Excel data here..."
                  disabled={importLoading}
                />
              </div>
              
              {parsedImportData.length > 0 && (
                <>
                  <div className="mb-3 p-3 bg-light border rounded">
                    <div className="d-flex align-items-center gap-4 flex-wrap">
                      {/* Exchange Rate Section */}
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold text-nowrap">
                          <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
                          Exchange Rate (VND/USD):
                        </span>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          style={{ width: '120px' }}
                          value={importExchangeRate}
                          onChange={(e) => setImportExchangeRate(e.target.value)}
                          placeholder="26000"
                          disabled={importLoading}
                        />
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleApplyExchangeRate}
                          disabled={importLoading || !importExchangeRate}
                          title="Apply exchange rate to all parsed records"
                        >
                          Apply
                        </button>
                      </div>
                      
                      {/* Duplicate Handling Section */}
                      {duplicateRecords.length > 0 && (
                        <>
                          <div className="vr" style={{ height: '30px' }}></div>
                          <div className="d-flex align-items-center gap-3">
                            <span className="fw-bold text-nowrap">
                              <FontAwesomeIcon icon={faCopy} className="me-2" />
                              Duplicates ({duplicateRecords.length}):
                            </span>
                            <div className="form-check form-check-inline mb-0">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="duplicateHandling"
                                id="duplicateSkip"
                                value="skip"
                                checked={duplicateHandling === 'skip'}
                                onChange={(e) => setDuplicateHandling('skip')}
                                disabled={importLoading}
                              />
                              <label className="form-check-label" htmlFor="duplicateSkip">
                                Skip
                              </label>
                            </div>
                            <div className="form-check form-check-inline mb-0">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="duplicateHandling"
                                id="duplicateOverride"
                                value="override"
                                checked={duplicateHandling === 'override'}
                                onChange={(e) => setDuplicateHandling('override')}
                                disabled={importLoading}
                              />
                              <label className="form-check-label" htmlFor="duplicateOverride">
                                Override
                              </label>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mb-3">
                    <h5>Preview ({parsedImportData.length} records):</h5>
                    <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                      <table className="table table-sm table-striped mb-0">
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                        <tr>
                          <th>Date</th>
                          <th>Description</th>
                          <th style={{ textAlign: 'right' }}>Amount VND</th>
                          <th style={{ textAlign: 'right' }}>Amount USD</th>
                          <th style={{ textAlign: 'right' }}>Tax %</th>
                          <th style={{ textAlign: 'right' }}>Tax VND</th>
                          <th style={{ textAlign: 'right' }}>Tax USD</th>
                          <th style={{ textAlign: 'right' }}>Exchange Rate</th>
                          <th style={{ textAlign: 'right' }}>Report Amount VND</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedImportData.map((record, index) => {
                          const reportAmountVnd = record.amount_vnd + record.tax_amount_vnd;
                          // Use the applied exchange rate (only updates when Apply is clicked)
                          const displayExchangeRate = appliedExchangeRate || currentRate?.vnd_to_usd || 25000;
                          return (
                            <tr key={index} className={record.isDuplicate ? 'table-warning' : ''}>
                              <td>{record.record_date}</td>
                              <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {record.description}
                                {record.isDuplicate && (
                                  <span className="badge bg-warning text-dark ms-2" title="This record appears to be a duplicate">Duplicate</span>
                                )}
                              </td>
                              <td style={{ textAlign: 'right' }}>{record.amount_vnd.toLocaleString()}</td>
                              <td style={{ textAlign: 'right' }}>{record.amount_usd.toFixed(1)}</td>
                              <td style={{ textAlign: 'right' }}>{record.tax_percent}%</td>
                              <td style={{ textAlign: 'right' }}>{record.tax_amount_vnd.toLocaleString()}</td>
                              <td style={{ textAlign: 'right' }}>{record.tax_amount_usd.toFixed(1)}</td>
                              <td style={{ textAlign: 'right' }}>{Math.round(displayExchangeRate).toLocaleString()}</td>
                              <td style={{ textAlign: 'right' }}>{reportAmountVnd.toLocaleString()}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={handleImportDialogClose}
                disabled={importLoading}
              >
                Cancel
              </button>
              {parsedImportData.length === 0 ? (
                <button 
                  className="btn btn-primary" 
                  onClick={handleImportProcess}
                  disabled={importLoading || !importData.trim()}
                >
                  {importLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSync} spin className="me-1" />
                      Processing...
                    </>
                  ) : (
                    'Parse Data'
                  )}
                </button>
              ) : (
                <div className="d-flex gap-2 align-items-center">
                  {duplicateRecords.length > 0 && (
                    <span className={`mt-2 ${duplicateHandling === 'override' ? 'text-danger' : 'text-warning'}`}>
                      <strong>{duplicateRecords.length}</strong> duplicate{duplicateRecords.length > 1 ? 's' : ''} will be {duplicateHandling === 'override' ? 'overridden' : 'skipped'}
                    </span>
                  )}
                  <button 
                    className="btn btn-success" 
                    onClick={handleImportConfirm}
                    disabled={importLoading}
                  >
                    {importLoading ? (
                      <>
                        <FontAwesomeIcon icon={faSync} spin className="me-1" />
                        Importing...
                      </>
                    ) : (
                      `Import ${duplicateHandling === 'override' ? parsedImportData.length : parsedImportData.length - duplicateRecords.length} Record${(duplicateHandling === 'override' ? parsedImportData.length : parsedImportData.length - duplicateRecords.length) !== 1 ? 's' : ''}`
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ImportPaymentsDialog
        mode="expense"
        isOpen={showExpenseImportDialog}
        onClose={() => setShowExpenseImportDialog(false)}
        onSuccess={() => { fetchRecords(); fetchCurrentBalance(); }}
        categories={categories}
        accountType={formData.accountType as AccountType}
        currentRate={currentRate}
      />
      {false && showExpenseImportDialog && (
        <div className="modal-overlay" style={{ paddingLeft: '250px' }}>
          <div className="modal-container" style={{ maxWidth: 'calc(100vw - 300px)', width: '90%', minWidth: '1000px', margin: '0 auto' }}>
            <div className="modal-header">
              <h3>Import Expense Payments</h3>
              <button 
                className="btn btn-sm btn-light modal-close-btn" 
                onClick={handleExpenseImportDialogClose}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label mb-0">Paste Excel Data (Tab-delimited):</label>
                  {parsedExpenseImportData.length > 0 && (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowExpenseTextarea(!showExpenseTextarea)}
                    >
                      <FontAwesomeIcon icon={showExpenseTextarea ? faChevronUp : faChevronDown} className="me-1" />
                      {showExpenseTextarea ? 'Hide' : 'Show'} Input
                    </button>
                  )}
                </div>
                {showExpenseTextarea && (
                  <>
                    <p className="text-muted small mb-2">
                      Copy data from Excel without headers. Expected columns: Date, Date Time, Record ID, Description, Amount (5th column)<br/>
                      <strong>Note:</strong> After parsing, you can edit description and select category for each record inline.
                    </p>
                    <textarea
                      className="form-control"
                      rows={8}
                      value={expenseImportData}
                      onChange={(e) => setExpenseImportData(e.target.value)}
                      placeholder="Paste your Excel data here..."
                      disabled={expenseImportLoading}
                    />
                  </>
                )}
              </div>
              
              {parsedExpenseImportData.length > 0 && (
                <>
                  <div className="mb-3 p-3 bg-light border rounded">
                    <div className="d-flex align-items-center gap-4 flex-wrap">
                      {/* Exchange Rate Section */}
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold text-nowrap">
                          <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
                          Exchange Rate (VND/USD):
                        </span>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          style={{ width: '120px' }}
                          value={expenseImportExchangeRate}
                          onChange={(e) => setExpenseImportExchangeRate(e.target.value)}
                          placeholder="26000"
                          disabled={expenseImportLoading}
                        />
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleApplyExpenseExchangeRate}
                          disabled={expenseImportLoading || !expenseImportExchangeRate}
                          title="Apply exchange rate to all parsed records"
                        >
                          Apply
                        </button>
                      </div>
                      
                      {/* Duplicate Handling Section */}
                      {expenseDuplicateRecords.length > 0 && (
                        <>
                          <div className="vr" style={{ height: '30px' }}></div>
                          <div className="d-flex align-items-center gap-3">
                            <span className="fw-bold text-nowrap">
                              <FontAwesomeIcon icon={faCopy} className="me-2" />
                              Duplicates ({expenseDuplicateRecords.length}):
                            </span>
                            <div className="form-check form-check-inline mb-0">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="expenseDuplicateHandling"
                                id="expenseDuplicateSkip"
                                value="skip"
                                checked={expenseDuplicateHandling === 'skip'}
                                onChange={(e) => setExpenseDuplicateHandling('skip')}
                                disabled={expenseImportLoading}
                              />
                              <label className="form-check-label" htmlFor="expenseDuplicateSkip">
                                Skip
                              </label>
                            </div>
                            <div className="form-check form-check-inline mb-0">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="expenseDuplicateHandling"
                                id="expenseDuplicateOverride"
                                value="override"
                                checked={expenseDuplicateHandling === 'override'}
                                onChange={(e) => setExpenseDuplicateHandling('override')}
                                disabled={expenseImportLoading}
                              />
                              <label className="form-check-label" htmlFor="expenseDuplicateOverride">
                                Override
                              </label>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mb-3">
                    <h5>Preview ({parsedExpenseImportData.filter(r => !r.excluded).length} of {parsedExpenseImportData.length} records) - Edit inline:</h5>
                    <div style={{ maxHeight: '450px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                      <table className="table table-sm table-striped mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                        <tr>
                          <th style={{ width: '40px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              title="Toggle all"
                              checked={parsedExpenseImportData.every(r => r.excluded)}
                              onChange={(e) => {
                                const allExcluded = e.target.checked;
                                const updatedRecords = parsedExpenseImportData.map(r => ({ ...r, excluded: allExcluded }));
                                setParsedExpenseImportData(updatedRecords);
                              }}
                            />
                          </th>
                          <th style={{ width: '90px' }}>Date</th>
                          <th style={{ width: 'auto', minWidth: '250px' }}>Description</th>
                          <th style={{ width: '140px' }}>Category</th>
                          <th style={{ width: '100px', textAlign: 'right' }}>Amount VND</th>
                          <th style={{ width: '80px', textAlign: 'right' }}>Amount USD</th>
                          <th style={{ width: '80px', textAlign: 'right' }}>Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedExpenseImportData.map((record, index) => {
                          const displayExchangeRate = expenseAppliedExchangeRate || currentRate?.vnd_to_usd || 25000;
                          const expenseCategories = categories.filter(cat => 
                            cat.type === 'expense' && cat.account_type === formData.accountType
                          );
                          return (
                            <tr key={index} className={record.isDuplicate ? 'table-warning' : (record.excluded ? 'table-secondary' : '')} style={{ opacity: record.excluded ? 0.6 : 1 }}>
                              <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                <input
                                  type="checkbox"
                                  checked={record.excluded || false}
                                  onChange={(e) => handleExpenseRecordEdit(index, 'excluded', e.target.checked)}
                                  title="Exclude from import"
                                />
                              </td>
                              <td style={{ fontSize: '0.85rem', verticalAlign: 'middle' }}>{record.record_date}</td>
                              <td style={{ padding: '4px' }}>
                                <textarea
                                  className="form-control form-control-sm"
                                  value={record.description}
                                  onChange={(e) => handleExpenseRecordEdit(index, 'description', e.target.value)}
                                  placeholder="Description"
                                  rows={2}
                                  style={{ 
                                    fontSize: '0.8rem', 
                                    lineHeight: '1.2',
                                    resize: 'vertical',
                                    minHeight: '40px'
                                  }}
                                />
                                {record.isDuplicate && (
                                  <span className="badge bg-warning text-dark mt-1" style={{ fontSize: '0.7rem' }} title="This record appears to be a duplicate">Dup</span>
                                )}
                              </td>
                              <td style={{ padding: '4px' }}>
                                <select
                                  className="form-select form-select-sm"
                                  value={record.category_id || ''}
                                  onChange={(e) => handleExpenseRecordEdit(index, 'category_id', parseInt(e.target.value))}
                                  style={{ 
                                    borderColor: !record.category_id ? '#dc3545' : undefined,
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  <option value="">Select...</option>
                                  {expenseCategories.map(cat => (
                                    <option key={cat.id} value={cat.id}>
                                      {cat.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td style={{ textAlign: 'right', fontSize: '0.85rem', verticalAlign: 'middle' }}>
                                {Math.abs(record.amount_vnd).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ textAlign: 'right', fontSize: '0.85rem', verticalAlign: 'middle' }}>
                                {Math.abs(record.amount_usd).toFixed(1)}
                              </td>
                              <td style={{ textAlign: 'right', fontSize: '0.85rem', verticalAlign: 'middle' }}>
                                {Math.round(displayExchangeRate).toLocaleString('en-US')}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={handleExpenseImportDialogClose}
                disabled={expenseImportLoading}
              >
                Cancel
              </button>
              {parsedExpenseImportData.length === 0 ? (
                <button 
                  className="btn btn-primary" 
                  onClick={handleExpenseImportProcess}
                  disabled={expenseImportLoading || !expenseImportData.trim()}
                >
                  {expenseImportLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSync} spin className="me-1" />
                      Processing...
                    </>
                  ) : (
                    'Parse Data'
                  )}
                </button>
              ) : (
                <div className="d-flex gap-2 align-items-center">
                  {expenseDuplicateRecords.length > 0 && (
                    <span className={`mt-2 ${expenseDuplicateHandling === 'override' ? 'text-danger' : 'text-warning'}`}>
                      <strong>{expenseDuplicateRecords.filter(r => !r.excluded).length}</strong> duplicate{expenseDuplicateRecords.filter(r => !r.excluded).length > 1 ? 's' : ''} will be {expenseDuplicateHandling === 'override' ? 'overridden' : 'skipped'}
                    </span>
                  )}
                  <button 
                    className="btn btn-success" 
                    onClick={handleExpenseImportConfirm}
                    disabled={expenseImportLoading}
                  >
                    {expenseImportLoading ? (
                      <>
                        <FontAwesomeIcon icon={faSync} spin className="me-1" />
                        Importing...
                      </>
                    ) : (
                      (() => {
                        const nonExcluded = parsedExpenseImportData.filter(r => !r.excluded);
                        const nonExcludedDuplicates = expenseDuplicateRecords.filter(r => !r.excluded);
                        const importCount = expenseDuplicateHandling === 'override' ? nonExcluded.length : nonExcluded.length - nonExcludedDuplicates.length;
                        return `Import ${importCount} Record${importCount !== 1 ? 's' : ''}`;
                      })()
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Copy from Previous Month Confirmation Dialog */}
      {showCopyDialog && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Copy from Previous Month</h3>
              <button 
                className="btn btn-sm btn-light modal-close-btn" 
                onClick={handleCopyDialogClose}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <p><strong>Account:</strong> {formData.accountType.charAt(0).toUpperCase() + formData.accountType.slice(1)}</p>
                <p className="mb-3">
                  This will copy records from one month to another with the following behavior:
                </p>
                <ul className="mb-3">
                  <li>Skip records that already exist (same description + category)</li>
                  <li>Recalculate amounts using current exchange rate</li>
                  <li>Update exchange rate reference to current rate</li>
                </ul>
              </div>
              
              <div className="mb-3">
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label"><strong>From Month:</strong></label>
                    <select 
                      className="form-select"
                      value={copyFromMonth}
                      onChange={(e) => setCopyFromMonth(e.target.value)}
                    >
                      {getAvailableMonths().map(month => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label"><strong>To Month:</strong></label>
                    <select 
                      className="form-select"
                      value={copyToMonth}
                      onChange={(e) => setCopyToMonth(e.target.value)}
                    >
                      {getAvailableMonths().map(month => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="mb-3">
                <label className="form-label"><strong>Select what to copy:</strong></label>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="copyOption"
                    id="copyExpenseOnly"
                    value="expense-only"
                    checked={copyOption === 'expense-only'}
                    onChange={(e) => setCopyOption(e.target.value as 'expense-only' | 'all')}
                  />
                  <label className="form-check-label" htmlFor="copyExpenseOnly">
                    <strong>Expense records only</strong>
                    <small className="d-block text-muted">Copy only expense records from previous month</small>
                  </label>
                </div>
                <div className="form-check mt-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="copyOption"
                    id="copyAll"
                    value="all"
                    checked={copyOption === 'all'}
                    onChange={(e) => setCopyOption(e.target.value as 'expense-only' | 'all')}
                  />
                  <label className="form-check-label" htmlFor="copyAll">
                    <strong>All records (Expense + Income)</strong>
                    <small className="d-block text-muted">Copy both expense and income records from previous month</small>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={handleCopyDialogClose}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleCopyDialogConfirm}
              >
                <FontAwesomeIcon icon={faCopy} className="me-2" />
                Copy Records
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Total records found */}
      <div className="records-count">
        {`Total records found: ${records.length}`}
      </div>
    </div>
  );
};

export default Records;
